/**
 * S5.4-F — die Seite „Kundenrechnungen“ (`/invoicing`). NUR LESEND.
 *
 * Die Kundensicht auf die Forge-Rechnungen: was heute ausgestellt wurde, was
 * noch offen ist, und worauf ein Mensch sehen muss. Sie liest genau drei
 * Endpunkte des in S5.4-B gebauten Lesepfads — sie schreibt nichts:
 *
 *   GET /api/invoicing/admin/kennzahlen   → die Kachelreihe (Berliner Tag)
 *   GET /api/invoicing/admin/warnungen    → die Warnleiste (sieben Klassen)
 *   GET /api/invoicing/admin/invoices     → die Liste (Filter + Pagination)
 *
 * 🔴 **„Kundenrechnungen“, nicht „Rechnungen“.** `/api/invoices` sind die
 *    LIEFERANTENrechnungen (Eingang); diese Fläche zeigt die von Forge
 *    ausgestellten AUSGANGSrechnungen an Kunden. Zwei verschiedene Dinge mit
 *    fast demselben Wort — die Beschriftung trennt sie.
 *
 * 🔴 **Die Filter stehen in der URL.** Neuladen und Teilen behalten sie. Jede
 *    Filteränderung setzt die Seite auf 1 zurück. Die SEITENZAHL der Anzeige
 *    kommt aber aus der ANTWORT (`liste.page`), nicht aus der URL: das Backend
 *    kappt `page` auf die letzte sinnvolle Seite (G8) — die URL wird dann
 *    nachgezogen.
 *
 * 🔴 **Kein Eingabefeld, kein Speichern.** Es gibt keine Aktion, die eine
 *    Rechnung ändert (Plan S5.5: „Keine Aktion, die die Rechnung ändert“).
 *
 * 🔴 **Jede Zeile öffnet das Detail** (`/invoicing/:id`, S5.5-F). Die Nummer
 *    ist ein echter Link (Tastatur, neuer Tab); der Klick auf die übrige Zeile
 *    ist Maus-Komfort. Mobil ist die ganze Karte der Link (`CardActionArea`).
 *
 * 🔴 **Zustände (Regel 16, Regel 22 §3):** Laden = Skeletons beim ersten Laden,
 *    nie Spinner (Anti-Pattern 11); beim Neuladen bleiben die alten Zeilen
 *    stehen und eine dezente Leiste zeigt die Arbeit an. Die Warnleiste meldet
 *    ihr erstes Laden mit einer Zeile („Warnungen werden geladen …“), damit
 *    eine hängende Anfrage nicht wie „nichts offen“ aussieht. Leer nennt den
 *    AUSSTELLUNGSMODUS nur, wenn er GELESEN wurde. Fehler zeigt einen Alert mit
 *    Handlung — für die Liste, die Kennzahlen UND die Warnleiste. „Unbekannt“
 *    ist nie 0 und nie `off`.
 *
 * 🔴 **Drei Reiter, Zustand in der URL** (S5.7-F, N6): „Rechnungen“ (diese Liste,
 *    Vorgabe), „Betrieb“ (`?tab=betrieb`, `Betrieb.jsx`) und
 *    „Refund-Entscheidungen“ (`?tab=refunds`, `RefundEntscheidungen.jsx`). Der
 *    Standardexport unten ist nur die Weiche; die Liste ist `RechnungenReiter`. Ein
 *    Reiterwechsel lässt die übrigen Parameter stehen — die Listenfilter überleben
 *    den Ausflug in einen anderen Reiter. Jeder Reiter fragt nur seine eigenen
 *    Endpunkte. Den `PageContainer` hält die Weiche: er bleibt beim Wechsel stehen,
 *    und seine Einblendung läuft nur beim ersten Seitenaufbau.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import PageContainer from '../../components/common/PageContainer'
import ViewHeader from '../../components/ViewHeader'
import UnderlineTabs from '../../components/common/UnderlineTabs'
import useListQuery from '../../components/common/listQuery/useListQuery'
import RefreshButton from '../../components/common/RefreshButton'
import StatTile from '../../components/common/StatTile'
import KpiRow, { KpiCell } from '../../components/common/KpiRow'
import TableFooterSimple from '../../components/common/TableFooterSimple'
import ListToolbar from '../../components/common/ListToolbar'
import EmptyState from '../../components/common/EmptyState'
import TableViewport from '../../components/common/TableViewport'
import MobileRecordCard from '../../components/common/MobileRecordCard'
import BottomSheetSelect from '../../components/common/BottomSheetSelect'
import Fachbegriff from '../../components/common/Fachbegriff'
import BottomSheetMultiSelect from '../../components/common/BottomSheetMultiSelect'
import Pill from '../../components/common/Pill'
import useOverflowMask from '../../components/common/useOverflowMask'
import { api } from '../../api/client'
import { FIELD_HEIGHT_SX, FS, PANEL_SX, SP, focusRing } from '../../theme/tokens'
import { formatBerlinDate } from '../../utils/time'
import { markiereSprungziel } from '../../utils/sprungziel'
// 🔴 Das Zeitformat der Kopfzeile kommt aus `utils/format.js` (Regel 15
//    §Format-Aufrufe) — `formatTime` druckt `de-DE` in `Europe/Berlin`.
import { formatTime } from '../../utils/format'
// 🔴 Die Bewegungssperre steht EINMAL im Haushelfer (R15 W1, 2026-11-01) —
//    `prefers-reduced-motion` samt `try/catch`, wie `Betrieb.jsx` sie nutzt.
import { scrollIntoViewRespectingMotion } from '../../utils/scroll'
import Betrieb from './Betrieb'
import RefundEntscheidungen from './RefundEntscheidungen'
// Die geschlossenen Mengen liegen in `beschriftungen.js`, weil der Reiter
// „Betrieb“ und die Detailseite sie auch brauchen — von hier geholt, entstünde
// ein Importkreis (InvoiceList → Betrieb → InvoiceList).
import {
  ART_OPTIONEN,
  BB_LABEL,
  GRUPPE_LABEL,
  KANAL_OPTIONEN,
  STATUS_LABEL,
  STATUS_TON,
  STEUERFALL_GLOSSAR,
  STEUERFALL_LABEL,
  STEUERFALL_OPTIONEN,
  STEUERFALL_TOOLTIP,
  gruppeHinweis,
  modusText,
} from './beschriftungen'

// ── Beschriftungen ─────────────────────────────────────────────────────────
// Jede Beschriftungstabelle deckt die GESCHLOSSENE Menge ihres Backends ganz —
// unabhängig davon, welche Werte als Filter angeboten werden. Der Gleichstand
// ist in `__tests__/InvoiceList.test.jsx` gegen die Quellen gepinnt.

const STRICH = '—'

/**
 * Die Kante der Z1-Leiste (R16 W4, Nachbesserung 2): ab hier steht sie in EINER
 * Zeile, darunter faehrt sie die kompakte Form (`ListToolbar.kompakt`).
 *
 * Sie war die **einzige** numerische Breakpoint-Angabe im Repo
 * (`grep -rn "breakpoints.down([0-9]" frontend/src` = 1 Treffer) und stand an
 * vier Stellen abgeschrieben (Kode, zwei Specs, Doku). Gemessen, nicht gewaehlt:
 * bei 1100 brachen Zaehler und ⟳ ab (`ueber: 124`), bei 1440 lagen zwischen
 * Suchfeld und „Status" 0 px Luft (Fotos `1100_liste-grund`, `1440_liste-*`;
 * Plan-Nachtrag Stufe 3, 2026-11-01).
 *
 * 🔴 Kein Theme-Token: die Zahl gehoert zu DIESER Leiste, nicht zum Designsystem
 *    (der Baustein kennt `kompakt` schon und fragt keine Breite).
 */
const Z1_BREIT_AB = 1360

/**
 * Das Glossar am Kopf „Customer" (R15 B3, Nachbesserung 2).
 *
 * Der deutsche Fachbegriff stand bis S3 am fokussierbaren Kopf „Tax case". Mit
 * dem Wegfall dieser Spalte wanderten Land und Steuerfall in die Metazeile unter
 * dem Kundennamen — und damit war der Begriff nur noch per Maus/Touch
 * erreichbar (`Fachbegriff … fokussierbar={false}` in den Wert-Zellen, richtig
 * so: eine Seite trägt bis zu 50 Zeilen, jede Zeile ein Tab-Stopp waere teuer).
 *
 * Der Kopf „Customer" traegt deshalb das Glossar der Spalte: denselben Text, den
 * „Tax case" trug (`STEUERFALL_GLOSSAR`, EINE Quelle), plus den Hinweis, wo die
 * beiden Werte jetzt stehen. Englisch wie die Seite, das Glossar selbst bleibt
 * deutsch (das ist der Zweck des Bausteins).
 */
const KUNDEN_GLOSSAR = `${STEUERFALL_GLOSSAR}. Country and tax case are shown in the second line.`

/**
 * Die Breite des DESKTOP-Aufklapp-Knopfs — `size="small"` ist 30 px (20 px
 * Symbol + 2×5 px Padding). Der Platzhalter einer Gruppe **ohne** Kinder trägt
 * dieselbe Breite, damit die Nummernspalte über Gruppen mit und ohne Knopf auf
 * EINER Flucht steht (R16 W1). Eine benannte Konstante, damit der Wert nicht
 * driftet, wenn das Theme das Knopf-Padding ändert (R16 H2).
 *
 * 🔴 Sie ist exportiert, weil die Kindzeile denselben Platz als LEERRAUM trägt
 *    (Flucht, B2-Feedback 2026-10-14) und die Probe diesen Gleichstand messen
 *    muss, ohne die 30 abzuschreiben — sonst driftet die Probe statt des Werts.
 */
export const AUFKLAPP_BREITE_DESKTOP = 30

/**
 * Die Trefferfläche des MOBILEN Aufklapp-Knopfs — Regel 22 §8 verlangt ≥ 44 px
 * für Touch (MUI-`medium` ist 40). Der Platzhalter ohne Kinder trägt dieselbe
 * Breite (R16 W1/W2).
 */
const AUFKLAPP_BREITE_MOBIL = 44

/**
 * Die DOM-ID der Kindzeile/-karte einer Gruppe — das Ziel von `aria-controls`
 * am Aufklapp-Knopf (R16 W3). Eine Rechnung steht in **genau einer** Gruppe,
 * die ID ist damit je Seite eindeutig.
 *
 * 🔴 `aria-controls` wird nur bei `offen` gesetzt (R16 W-N1): die Kindzeilen
 * werden erst dann gerendert (`{offen && …}`), zugeklappt zeigte der Verweis ins
 * Leere — ein ungültiges ARIA-Attribut, dieselbe Konvention wie an der
 * Reiterleiste dieser Datei (`aria-controls` nur am gewählten Reiter).
 */
const kinderId = (k) => `gruppe-kind-${k.id}`

/** `bb_state` und `status` — weitergereicht, die Gleichstandstests lesen sie von hier. */
export { BB_LABEL, STATUS_LABEL }

/**
 * Die Status-FILTER — nur Zustände, die heute ein Schreiber im Backend erreicht
 * (grep über `backend/`, 2026-10-02):
 *   intent        Spaltenvorgabe (`invoicing/models.py:48`)
 *   issuing/issued `issuance.py:681/728`, `korrektur.py:926/971`
 *   mailed        `mailing.py:603`   ·   undeliverable  `mailing.py:581`
 *   corrected     `korrektur.py:993`, `eingang.py:805`
 * `paid`, `closed` und `failed` stehen im CHECK, haben aber KEINEN Schreiber —
 * ein Filter darauf läge immer leer und sähe aus wie „geprüft, nichts da“. Ihre
 * Beschriftung steht trotzdem in `beschriftungen.js` (falls eine Zeile sie trägt).
 *
 * 🔴 Pflege: `cancelled` HAT seit S6.1d einen Schreiber — rohes SQL in
 *    `scripts/rechnung_handfall_abschliessen.py` (`UPDATE invoice SET status =
 *    'cancelled'`). Er gehört deshalb HIER hinein; das steht noch aus (eigener PR
 *    mit Regel-16-Gate, VOR dem ersten scharfen Lauf):
 *    `docs/backlog-auto-repair/2026-10-03-invoice-cancelled-statusfilter-und-waechter.md`.
 *    Der Test „die Status-Filter decken GENAU die Zustände, die ein Schreiber in
 *    backend/invoicing/ erreicht“ sieht diesen Schreiber NICHT (er scannt nur
 *    `backend/invoicing/*.py` und kein rohes SQL) und bleibt deshalb grün — die
 *    Erweiterung des Scans steht im selben Backlog-Eintrag.
 */
const STATUS_FILTER = ['intent', 'issuing', 'issued', 'mailed', 'undeliverable', 'corrected']
export const STATUS_OPTIONEN = STATUS_FILTER.map((value) => ({ value, label: STATUS_LABEL[value] }))

// Status-Ton, Kanal und Steuerfall: `beschriftungen.js` (Kontrastzahlen stehen dort).

/**
 * `mailstatus` — die geschlossene Menge aus `lagen.MAILSTATUS_WERTE`.
 * `entfaellt` heisst: hier ist KEINE Mail zu erwarten (unterdrückt, oder eine
 * Dokumentart ohne Mailweg); `keins`: es gab (noch) keine Mail, weil das
 * Dokument nicht ausgestellt ist. 🔴 Eine **gemailte Korrektur** ist seit A5
 * (2026-10-13) `zugestellt` — sie wird seit N10-T2 gemailt.
 */
export const MAIL_LABEL = {
  zugestellt: 'Delivered',
  abgelehnt: 'Rejected',
  problem: 'Problem',
  ausstehend: 'Pending',
  entfaellt: 'Not applicable',
  keins: 'No mail',
}
const MAIL_TON = {
  zugestellt: 'ok',
  abgelehnt: 'err',
  problem: 'warn',
  ausstehend: 'info',
  entfaellt: 'plain',
  keins: 'plain',
}

/** Die sieben Warnklassen (`lagen.WARNKLASSEN`) — schlimmste zuerst. */
const WARN_TITEL = {
  ausstellung: 'Issuance errors',
  mail: 'Mail problems',
  plausibilitaet: 'Plausibility warnings',
  bb: 'BB deviations',
  dispute: 'Payment disruptions',
  // 🔴 R16 W1: die Klasse `refund` bündelt die **offenen** Refund-Entscheidungen UND
  // den Unterfall `lexware_ende_fehlt` (der still erledigte Zeilen zeigt). „Offene …"
  // passte für den Unterfall nicht — der Titel ist deshalb offen formuliert.
  refund: 'Refund decisions',
  // Vergangenheits-Wächter (Orchestrator-Entscheid 12.10.2026). Der Titel hängt am
  // Modus und wird unten in `notices` gesetzt: im Modus `off` ist die Zahl die GEFAHR
  // (beim Umschalten würde ausgestellt), in `pilot`/`live` ist sie ein RÜCKSTAND.
  stichtag: 'Cut-off date in the past',
}
const WARN_SCHWERE = {
  ausstellung: 'error',
  mail: 'error',
  plausibilitaet: 'warning',
  bb: 'warning',
  dispute: 'warning',
  refund: 'warning',
  // Keine Sperre, sondern eine Warnung — deshalb `warning`, nicht `error`.
  stichtag: 'warning',
}
// Die Reihenfolge, in der Warnklassen gezeigt werden — höher zuerst. `dispute`(2) steht
// über `refund`(1): ein Chargeback ist eine offene Geldforderung mit Frist und wiegt
// schwerer als eine offene Refund-Entscheidung (R16 Randnotiz). `stichtag`(7) steht in
// seiner Schwere-Bande vorn: sie warnt als einzige vor einer unumkehrbaren Handlung
// (Umschalten auf `live` mit einem Stichtag in der Vergangenheit) — Orchestrator-Entscheid
// 12.10.2026. 🔴 Der Rang allein stellt sie NICHT an die Spitze — `NoticeSlot` sortiert
// erst die Schwere-Bande (`error` vor `warning`), dann `priority`; ein gefülltes
// `ausstellung`/`mail` steht deshalb VOR ihr. Unverlierbar macht sie `pinned: true`
// (unten in `notices`): angeheftete Hinweise sind vom Deckel ausgenommen und stehen nie
// hinter „+n more notices" (R16 B1).
const WARN_RANG = {
  stichtag: 7, ausstellung: 6, mail: 5, plausibilitaet: 4, bb: 3, dispute: 2, refund: 1,
}

/**
 * Ab welcher Zahl die Klasse `stichtag` im Modus `pilot`/`live` gezeigt wird.
 *
 * 🔴 Orchestrator-Entscheid 12.10.2026 (W5). Im Modus `off` ist JEDE unmarkierte Zeile
 * ein Signal — der Verbraucher läuft gar nicht, also wird nichts verbraucht. In
 * `pilot`/`live` läuft er im 2-Minuten-Takt; ein Rückstand von wenigen Zeilen ist dort
 * normal, und die Live-Formulierung („Stichtag prüfen") zeigte sonst bei jedem Rückstand
 * in die falsche Richtung. **Im Modus `off` gilt die Schwelle NICHT** (dort ab 1).
 */
const STICHTAG_SCHWELLE_AKTIV = 5

/** Die Reihenfolge der Filter in der URL — genau diese Schlüssel werden übertragen. */
const FILTER_SCHLUESSEL = [
  'von', 'bis', 'kanal', 'steuerfall', 'land', 'art',
  'betrag_min', 'betrag_max', 'q',
]
/** Die Felder hinter dem Filter-Öffner (Desktop); auf Mobil liegt ALLES dort. */
const SHEET_SCHLUESSEL = ['land', 'von', 'bis', 'betrag_min', 'betrag_max']

const PRO_SEITE = 50

/** Eine Tastatureingabe wird erst nach dieser Ruhezeit in die URL geschrieben. */
const DROSSEL_MS = 350

const gleich = (wert) => wert
const normalisiereLand = (wert) => wert.trim().toUpperCase()
const normalisiereBetrag = (wert) => wert.trim().replace(',', '.')
/**
 * Ein Betrag ist leer oder eine Dezimalzahl (Komma ODER Punkt, Vorzeichen
 * erlaubt — Korrekturen tragen negative Summen). Alles andere lehnte das
 * Backend mit 400 ab; hier wird es gar nicht erst gefragt.
 */
const pruefeBetrag = (wert) => (
  wert.trim() === '' || /^-?\d+(?:[.,]\d+)?$/.test(wert.trim())
    ? null
    : 'A number only, e.g. 49,90'
)

/** Betrag ohne Währungssymbol — die Liste liefert keine Währung (siehe invoicing.md). */
const zahl = (wert) => (wert == null
  ? STRICH
  : new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(wert))

/**
 * Der Seitenbereich der Fusszeile fuer den Fall „alles auf EINER Seite".
 *
 * 🔴 S3-NB1 (Z3): `TableFooterSimple` zeichnet seinen Blätterer nur ueber einer
 *    Seite (`count > rowsPerPage`) — darunter blieb die Leiste leer, obwohl sie
 *    44 px hoch steht. Form und Zeichen sind die von MUI
 *    (`labelDisplayedRows` schreibt `${from}–${to} of ${count}` mit
 *    Halbgeviertstrich, ohne Tausenderpunkt), damit die Leiste in beiden
 *    Zustaenden dieselbe Sprache spricht. `null` heisst: der Blätterer traegt
 *    die Zeile, ein zweiter Text waere eine zweite Anzeige.
 */
const seitenBereich = (gesamt, proSeite) => (
  Number.isFinite(gesamt) && gesamt > 0 && gesamt <= proSeite
    ? `1–${gesamt} of ${gesamt}`
    : null
)

function StatusPill({ status }) {
  if (!status) return <Typography component="span" sx={{ color: 'text.secondary' }}>{STRICH}</Typography>
  return (
    <Pill tone={STATUS_TON[status] || 'plain'} bordered nowrap data-testid="status-pill">
      {STATUS_LABEL[status] || status}
    </Pill>
  )
}

function MailPill({ wert }) {
  if (!wert) return <Typography component="span" sx={{ color: 'text.secondary' }}>{STRICH}</Typography>
  return (
    <Pill tone={MAIL_TON[wert] || 'plain'} bordered nowrap data-testid="mail-pill">
      {MAIL_LABEL[wert] || wert}
    </Pill>
  )
}

/**
 * Der Ton einer **Throughput**-Marke (S3, §2.1): nominal ist `plain`, nur die
 * Abweichung trägt einen Ton (Akzeptanz 5). Nominal heisst beim BB-Zustand
 * „geschrieben" oder „nicht nötig"; alles andere ist ein Zwischen- oder
 * Fehlerzustand und trägt `warn` bzw. `err`.
 *
 * 🔴 Bewusst eine ABLEITUNG und keine zweite Beschriftungsmap (§6 Nr. 3): die
 *    Wörter stehen unverändert in `BB_LABEL`, hier entsteht nur die Farbe.
 * 🔴 `STATUS_TON` bleibt unangetastet — die Tabelle ist mit der Detailseite
 *    geteilt und gegen Kontrastmessungen gepinnt (§4.4).
 */
const bbTon = (wert) => {
  if (!wert) return 'plain'
  if (wert === 'posted' || wert === 'nicht_noetig') return 'plain'
  if (wert === 'upload_abgewiesen' || wert === 'posting_blocked') return 'err'
  return 'warn'
}

/**
 * Die Kunden-Zelle samt **Metazeile** (S3, §2.1): `Country` und `Tax case` sind
 * keine eigenen Spalten mehr, sie stehen klein unter dem Namen. Damit faellt die
 * Tabelle von acht auf sechs Spalten.
 *
 * 🔴 Der Trenner „ · " gehoert zum FOLGENDEN Teil (Lehre 5): das Land steht in
 *    einem `nowrap`-Span, der Punkt hängt nicht am Zeilenende.
 */
function KundenZelle({ z, mitDatum = false }) {
  return (
    <TableCell sx={{ fontSize: FS.sm }}>
      <Box>{z.kunde || STRICH}</Box>
      <Typography component="div" variant="caption" sx={{ color: 'text.secondary' }}>
        {/* 🔴 S3-NB1 (Z6, Nachtrag 01.11.): unterhalb 1360 px faellt die
            Datumsspalte weg (die Nummer brach bei 900 px dreizeilig, und sechs
            Spalten passten nicht). Das Datum steht dann HIER, in derselben
            Reihenfolge wie auf der Karte (Plan §2.1, „390"): Datum · Land ·
            Steuerfall. */}
        {mitDatum && (
          <>
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              {formatBerlinDate(z.datum, { dateOnly: true })}
            </Box>
            {' · '}
          </>
        )}
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{z.land || STRICH}</Box>
        {' · '}
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {/* Wert-Zelle: Tooltip fuer Maus/Touch, KEIN Tab-Stopp — das
              Glossar haengt am fokussierbaren Kopf „Customer" (R16 N1; seit
              der Nachbesserung 2 traegt ihn dieser Kopf, vorher „Tax case"). */}
          {z.steuerfall
            ? <Fachbegriff label={STEUERFALL_LABEL[z.steuerfall] || z.steuerfall}
                           begriff={STEUERFALL_TOOLTIP[z.steuerfall] || 'Steuerfall'}
                           fokussierbar={false} />
            : STRICH}
        </Box>
      </Typography>
    </TableCell>
  )
}

/**
 * Die **Throughput**-Zelle (S3, §2.1): BB-Zustand und Mailstatus waren zwei
 * Spalten und sind jetzt **eine** Zelle mit zwei beschrifteten Marken.
 *
 * Der **virtuelle** Kopf einer kopflosen Gruppe (Lexware) trägt lauter `null` —
 * `StatusPill(null)`, `MailPill(null)` und der BB-Zweig zeichnen dann den Strich,
 * keine erfundene Null (Regel 22 §3).
 */
function ThroughputZelle({ z }) {
  return (
    <TableCell sx={{ fontSize: FS.sm }}>
      <Stack direction="row" spacing={SP.chip} alignItems="center" useFlexGap flexWrap="wrap">
        <Pill tone={bbTon(z.bb_state)} bordered nowrap>
          {`BB: ${z.bb_state ? (BB_LABEL[z.bb_state] || z.bb_state) : STRICH}`}
        </Pill>
        <MailPill wert={z.mailstatus} />
      </Stack>
    </TableCell>
  )
}

/** Das Datum einer Zeile — eigene Spalte (S3: sechs Spalten). */
function DatumZelle({ z }) {
  return (
    <TableCell sx={{ fontSize: FS.sm }}>{formatBerlinDate(z.datum, { dateOnly: true })}</TableCell>
  )
}

/**
 * Die **Geld**-Spalte (S3, §2.1): `Gross` primär, bei Korrekturen eine zweite
 * Zeile `Remaining …`. Der Gruppenhinweis trägt deshalb nur noch „2 corrections"
 * — die Zahl stand sonst zweimal auf derselben Zeile.
 */
function BetragZelle({ z, rest }) {
  return (
    <TableCell align="right" sx={{ fontSize: FS.sm }}>
      <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{zahl(z.brutto)}</Box>
      {rest != null && (
        <Typography component="div" variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {`Remaining ${rest}`}
        </Typography>
      )}
    </TableCell>
  )
}

// Der Ton einer Warnpille — aus der Schwere, nicht aus einer zweiten Tabelle.
const WARN_PILL_TON = { error: 'err', warning: 'warn', info: 'info' }

/**
 * Die DOM-ID der aufgeklappten Belegliste unter der Warnpille — das Ziel von
 * `aria-controls` (R16 C1). Die Warnleiste steht **einmal** auf der Seite, die
 * ID ist damit eindeutig; sie steht als Konstante da, weil zwei Stellen sie
 * brauchen (der Aufklapper und das Ziel).
 */
const WARN_BEISPIELE_ID = 'warnleiste-beispiele'
const SCHWERE_RANG = { error: 3, warning: 2, info: 1 }
// Die Klassen, deren Beispiele echte Rechnungs-IDs sind und deshalb auf das
// Detail zeigen duerfen. `refund` mischt Rechnungs- UND Refund-IDs
// (`lagen.py:829-834`), `stichtag` traegt Bestell-IDs (`lagen.py:849-850`) —
// beide bekommen deshalb KEINEN Link, sondern ihren Sprung (§6 Nr. 5).
const WARN_LINK_KLASSEN = new Set(['ausstellung', 'mail', 'plausibilitaet', 'bb'])

/**
 * Die **Warnleiste als Pillenzeile** (S3, §2.1, W11).
 *
 * Jede Pille traegt Klasse und Zahl; ein Klick oeffnet **direkt unter der
 * Warnleiste** eine eigene Disclosure mit den Belegen der Klasse. Sie steht
 * ausdruecklich NICHT in Z3: Z3 gehoert der `ActiveFilterBar`, zwei Semantiken
 * in einer Zone waeren ein Fehler.
 *
 * 🔴 Verlinkt wird **je Klasse, nie pauschal** (§6 Nr. 5, entschieden 01.11.):
 *    `ausstellung`/`mail`/`plausibilitaet`/`bb` → `/invoicing/<id>`; `dispute`
 *    nur bei nicht-null `invoice_id` (darf NULL sein); `refund` und `stichtag`
 *    bekommen **keinen** Link, sondern ihren Sprung (Refund-Reiter bzw.
 *    Operations). Damit ist B-1 ohne Backend gelöst.
 * 🔴 Die Marke `warnleiste` bleibt (§4.3 Nr. 3): die Kachel „Open blocks" springt
 *    hierher, und ein Fall verlangt sie ausdruecklich.
 */
function Warnleiste({
  leisteRef, pillen, laedt, offene, setOffene, zuRefunds, zuBetrieb, kompakt = false,
}) {
  const gewaehlt = pillen.find((p) => p.klasse === offene) || null
  // 🔴 S3-NB1 (Z7, Nachtrag 01.11.): unterhalb 1360 px stehen die Pillen in
  //    EINER rollenden Zeile statt in vier umbrechenden (Foto
  //    `390_liste-warnungen-alle_klick`). Die weiche Kante zeigt an, dass rechts
  //    noch etwas liegt — sonst liest sich die abgeschnittene Zeile als
  //    vollstaendig (`useOverflowMask`, dieselbe Hausform wie Z3/Tabs).
  //    `deps: [pillen.length]` misst neu, wenn Pillen dazukommen — der
  //    `ResizeObserver` sieht nur den Kasten, nicht den Inhalt.
  const rollRef = useRef(null)
  const { maskSx } = useOverflowMask(rollRef, { deps: [pillen.length] })
  return (
    // 🔴 R16-W3, Nachtrag S3 (04.11.2026): der Ring haengt am SPRUNG-MERKMAL
    //    `data-sprungziel` (gesetzt von `springeZu`), nicht an `:focus` — sonst
    //    traegt die Leiste einen Rahmen, sobald ein gewoehnlicher Klick auf
    //    nicht fokussierbaren Inhalt sie fokussiert. Dieselbe Form wie
    //    `liste-panel` und `Betrieb.jsx`; Mechanik in `utils/sprungziel.js`.
    <Box
      ref={leisteRef}
      tabIndex={-1}
      sx={{ '&[data-sprungziel="aktiv"]': focusRing() }}
      aria-busy={laedt ? 'true' : 'false'}
      data-testid="warnleiste"
    >
      {/* 🔴 Solange /warnungen laedt, sagt die Leiste das. Ohne diese Zeile saehe
          eine haengende Anfrage genauso aus wie „geprueft, nichts offen". */}
      {laedt && (
        <Typography variant="caption" role="status" sx={{ display: 'block', color: 'text.secondary' }}>
          Warnings are loading …
        </Typography>
      )}
      {pillen.length > 0 && (
        <Stack
          ref={rollRef}
          direction="row"
          spacing={SP.chip}
          useFlexGap
          data-testid="warnleiste-pillen"
          sx={{
            // 🔴 Die rollende Fassung traegt `nowrap` — die Pillen selbst sind
            //    schon `nowrap`, hier haelt die ZEILE sie in einer Reihe.
            ...(kompakt
              ? { flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', maxWidth: '100%', ...maskSx }
              : { flexWrap: 'wrap' }),
          }}
        >
          {pillen.map((p) => (
            <Pill
              key={p.klasse}
              tone={WARN_PILL_TON[p.schwere] || 'info'}
              count={p.anzahl}
              nowrap
              selected={offene === p.klasse}
              // 🔴 R16 C1 (Nachbesserung 2): die Pille ist ein AUFKLAPPER, kein
              //    Umschalter. Ohne `aria-expanded` loest sie eine Einfuegung
              //    aus, die sie nicht ansagt (WCAG 4.1.2). Das Hausmuster steht
              //    in derselben Datei an der Gruppenzeile.
              aria-expanded={offene === p.klasse}
              //    `aria-controls` nur, solange der Block steht — ein Verweis
              //    auf ein nicht existierendes Ziel ist schlechter als keiner.
              aria-controls={offene === p.klasse ? WARN_BEISPIELE_ID : undefined}
              // 🔴 `selected` bleibt fuer die OPTIK, aber `Pill` leitet daraus
              //    `aria-pressed` ab (`Pill.jsx:197`). Ein Aufklapper, der sich
              //    zugleich als Umschalter ausgibt, sagt zwei verschiedene
              //    Dinge an. `...rest` steht in `Pill.jsx:335` NACH
              //    `aria-pressed` — das ausdrueckliche `undefined` hier gewinnt.
              aria-pressed={undefined}
              // 🔴 R16 B-1 (Nachbesserung 3): die Pille SIEHT wie eine
              //    Statusmarke aus, obwohl sie aufklappt. Die Marke ist dieselbe
              //    wie an der Gruppenzeile derselben Datei (`:1615`) und folgt
              //    demselben Zustand wie `aria-expanded` — vor dem ersten Klick
              //    steht dort `ExpandMore`.
              // 🔴 S3-NB5 (R16 W1): die Marke traegt die HAUSGROESSE eines
              //    Pill-Icons (`FS.base`, Vorbild `Bestand.jsx:172`), nicht die
              //    MUI-Vorgabe `small` (20 px). 20 px hob die Innenhoehe der
              //    Pille von 17,5 auf 20 px — keine andere Pille im Repo stand
              //    auf 20 px (`SituationHeader.jsx:584` = `FS.sm`).
              icon={offene === p.klasse
                ? <ExpandLess sx={{ fontSize: FS.base }} />
                : <ExpandMore sx={{ fontSize: FS.base }} />}
              onClick={() => setOffene(offene === p.klasse ? null : p.klasse)}
            >
              {p.titel}
            </Pill>
          ))}
        </Stack>
      )}
      {gewaehlt && (
        <Box id={WARN_BEISPIELE_ID} data-testid="warnleiste-beispiele" sx={{ mt: SP.tight }}>
          {gewaehlt.hinweis && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: SP.chip }}>
              {gewaehlt.hinweis}
            </Typography>
          )}
          {gewaehlt.klasse === 'refund' && (
            <Button size="small" onClick={zuRefunds}>To the refund decisions</Button>
          )}
          {gewaehlt.klasse === 'stichtag' && (
            <Button size="small" onClick={zuBetrieb}>To Operations</Button>
          )}
          {WARN_LINK_KLASSEN.has(gewaehlt.klasse) && (
            <Stack direction="row" spacing={SP.chip} useFlexGap flexWrap="wrap">
              {gewaehlt.beispiele.filter((id) => id != null).map((id) => (
                <Link key={id} component={RouterLink} to={`/invoicing/${id}`} sx={{ fontSize: FS.sm }}>
                  {`#${id}`}
                </Link>
              ))}
            </Stack>
          )}
          {gewaehlt.klasse === 'dispute' && (
            <Stack direction="row" spacing={SP.chip} useFlexGap flexWrap="wrap">
              {gewaehlt.beispiele.filter((id) => id != null).map((id) => (
                <Link key={id} component={RouterLink} to={`/invoicing/${id}`} sx={{ fontSize: FS.sm }}>
                  {`#${id}`}
                </Link>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  )
}

/**
 * Der Leerzustand der Liste (S3, Akzeptanz 6): `EmptyState` mit `reason`.
 *
 * 🔴 Die Vorgabetexte von `EmptyState` sind DEUTSCH; diese Flaeche ist englisch
 *    (L4). Deshalb traegt jeder Zweig hier seine eigene `message` — die
 *    Tonalitaet (Farbe, `role="alert"` beim Fehler) kommt weiter aus `reason`.
 * 🔴 `leerTitle` (der Env-Name bei `INVOICING_ISSUE_MODE=off`) bleibt als
 *    `title` am Kasten erhalten — er war schon vorher nur ein Tooltip.
 */
function Leerzustand({
  hatFilter, leerTitle, leerText, leerNenntTestbelege,
  filterZuruecksetzen, schalteTestbelegeEin, q, sucheLeeren,
}) {
  // 🔴 S3-NB1 (Z9, Nachtrag 01.11.): mit Suchbegriff ZITIERT der Leerzustand
  //    ihn (Foto `1440_liste-kein-treffer` zeigte „No matches for these
  //    filters." ohne Begriff — der Leser erfuhr nicht, wonach gesucht wurde).
  //    Hausform ist `EmptyState`s eigenes Echo (`query` + `noun`, G0.2/R16-B1,
  //    gegen `messeP9` gegengelesen: der Text muss den Begriff tragen). Ohne
  //    Begriff bleibt der alte Satz — „these filters" nennt dann die Filter.
  const inhalt = hatFilter ? (
    <EmptyState
      reason="no-match"
      message={q ? undefined : 'No matches for these filters.'}
      query={q || undefined}
      noun="invoice"
      onClearSearch={q ? sucheLeeren : undefined}
      detail={leerText}
      onClearFilters={filterZuruecksetzen}
    />
  ) : (
    <EmptyState
      reason="no-data"
      message={leerNenntTestbelege ? 'No productive invoices yet.' : 'No invoices yet.'}
      detail={leerText}
      action={leerNenntTestbelege ? (
        <Button size="small" onClick={schalteTestbelegeEin}>Show test invoices</Button>
      ) : undefined}
    />
  )
  return leerTitle ? <Box title={leerTitle}>{inhalt}</Box> : inhalt
}

/** Die Kopf-Referenz einer kopflosen Gruppe: „Lexware \<Nummer\>" oder der Strich. */
const kopfText = (kopf, bezug) => (
  (kopf.nummer || bezug) || 'without number'
)

/** Der Chip, der die Korrektur an ihr Original hängt (B1s `beleg_bezug`, B2). */
const bezugText = (bezug) => {
  if (!bezug) return null
  if (bezug.art === 'original') return `Correction of ${bezug.beleg_nummer || bezug.beleg_id}`
  if (bezug.art === 'lexware') return `Lexware ${bezug.nummer}`
  return null
}

/**
 * Die Marke eines Gruppenkopfs (B2) — nur wo sie etwas sagt. Ein `order`-Kopf
 * trägt seine Rechnungsnummer, ein `lexware`-Kopf seine „Lexware \<Nummer\>";
 * erst ein **mehrdeutiger** Name oder ein Kopf ohne beides braucht ein Wort.
 */
const gruppeMarke = (g) => {
  if (g.mehrdeutig) return 'Ambiguous name'
  if (g.gruppe_art === 'order' || g.gruppe_art === 'lexware') return null
  return GRUPPE_LABEL[g.gruppe_art] || g.gruppe_art
}

/**
 * Ein Textfilter, der lokal tippt und GEDROSSELT in die URL schreibt.
 *
 * Ohne die Drosselung liefe je Tastendruck eine Datenbankabfrage. Die URL bleibt
 * die Wahrheit: ändert sie sich von aussen (Zurücksetzen, geteilter Link), zieht
 * das Feld nach — es sei denn, die Eingabe meint schon denselben Wert („de“ für
 * `DE`, „49,90“ für `49.90`), dann bleibt stehen, was getippt wurde.
 *
 * `pruefe` liefert einen Fehlertext oder `null`. Bei einem Fehler wird NICHTS
 * geschrieben: keine Anfrage mit einem Wert, den der Server ablehnen würde.
 *
 * ⚠️ Bekannte Grenze (zurückgestellt, `invoicing.md` §S5.4-F): trifft ein
 *    Tastendruck in genau den Commit, in dem der gedrosselte Wert in die URL
 *    geschrieben wird, kann ihn das Nachziehen verwerfen.
 */
function useGedrosselterFilter(schluessel, searchParams, setzeFilter, normalisiere = gleich, pruefe = null) {
  const ausUrl = searchParams.get(schluessel) || ''
  const [eingabe, setEingabe] = useState(ausUrl)
  useEffect(() => {
    setEingabe((alt) => (normalisiere(alt) === ausUrl ? alt : ausUrl))
  }, [ausUrl, normalisiere])
  const fehler = pruefe ? pruefe(eingabe) : null
  useEffect(() => {
    if (fehler) return undefined
    const ziel = normalisiere(eingabe)
    if (ziel === ausUrl) return undefined
    const t = setTimeout(() => setzeFilter(schluessel, ziel), DROSSEL_MS)
    return () => clearTimeout(t)
  }, [eingabe, ausUrl, fehler, normalisiere, schluessel, setzeFilter])
  return [eingabe, setEingabe, fehler]
}

/**
 * Der Reiter „Rechnungen“ — die Liste aus S5.4-F.
 *
 * 🔴 **S2 (2026-11-01): die KPI-Reihe ist Z0 DIESES Panels.** Sie stand bis
 *    dahin zwischen Kopf und Reitern und galt damit für alle drei Reiter; sie
 *    ist aber eine Rechnungs-Kennzahl, also gehört sie unter die Reiter in den
 *    Reiter, den sie beschreibt. Der ABRUF bleibt in der Schale (der Modus-Chip
 *    im Kopf hängt an derselben Antwort und steht auf allen drei Reitern) —
 *    dieser Reiter bekommt `kennzahlen`/`kennzahlenLaedt`/`kennzahlenFehler`
 *    als Prop und frischt über `ladeKennzahlen` auf.
 *
 *    `setzeTestbelege` kommt ebenfalls von der Schale: **eine** Schreibstelle
 *    für den URL-Zustand, hier nur ein Bedienelement (F2.7).
 */
/**
 * Ein Anker scrollt und setzt den Fokus.
 *
 * 🔴 Die Bewegungssperre steht im HAUSHELFER (`utils/scroll.js`) — er trägt die
 *    `prefers-reduced-motion`-Abfrage UND das `try/catch` für Umgebungen ohne
 *    `matchMedia`; hier nicht ein zweites Mal (R15 W1 / R16 Warnung 1,
 *    2026-11-01). Bis dahin baute diese Datei die Abfrage als siebte Kopie neu,
 *    mit einem Kommentar, der „dieselbe Form wie `Betrieb.springeZu`“ behauptete
 *    — die dort delegiert. jsdom kennt `scrollIntoView` nicht; der Helfer steigt
 *    dann still aus, der Fokus ist der tragende Teil.
 */
function springeZu(ziel) {
  if (!ziel) return
  // 🔴 R16-W3, Nachtrag S3 (04.11.2026): der Ring haengt am SPRUNG-MERKMAL,
  //    nicht an `:focus` — sonst zeichnet ein gewoehnlicher Klick auf nicht
  //    fokussierbaren Inhalt der Leiste einen Rahmen. Merkmal und Fokus im
  //    SELBEN Schritt, am selben Knoten (dieselbe Form wie `panelSprung`).
  markiereSprungziel(ziel)
  scrollIntoViewRespectingMotion(ziel, { block: 'start' })
  ziel.focus({ preventScroll: true })
}

function RechnungenReiter({
  kennzahlen, kennzahlenLaedt, kennzahlenFehler, ladeKennzahlen, setzeTestbelege,
  zuRefunds, zuBetrieb,
}) {
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'))
  // 🔴 S3-NB1 (§2.1, Nachtrag 01.11.): die Leiste ist ab 1360 px EINE Zeile;
  //    darunter (1280, 1100, 900, 390) faehrt sie die KOMPAKTE Form — Facetten
  //    im Blatt, Zaehler in Z3. Die Schwelle ist eine Zahl am Aufrufer, nicht
  //    eine zweite Media-Query im Baustein (`ListToolbar.kompakt`, rein additiv).
  //    🔴 Warum 1360 und nicht 1100 (gemessen 01.11. an der Kette `rechs3`):
  //    bei 1100 standen Zaehler und ⟳ abgeschnitten (`ueber: 124`), bei 1440
  //    lagen zwischen Suchfeld und „Status" 0 px Luft, und die Facette „Status"
  //    schob sich ueber das Suchfeld (Fotos `1100_liste-grund`, `1440_liste-*`).
  const istKompakt = useMediaQuery(theme.breakpoints.down(Z1_BREIT_AB))
  const [searchParams, setSearchParams] = useSearchParams()
  // 🔴 Testbelege (2026-10-31): derselbe URL-Zustand wie der Seitenschalter (F2.7).
  const testbelege = searchParams.get('testbelege') === '1'
  const navigate = useNavigate()

  // Der Zeilenklick ist Maus-Komfort; der Link in der Nummernspalte bleibt der
  // eigentliche Weg. Nicht doppelt navigieren (Klick AUF den Link), nicht bei
  // Modifikatortasten (neuer Tab gehört dem Link) und nicht, wenn jemand Text
  // markiert hat. Für den `RouterLink` genügt `defaultPrevented` (er ruft
  // `preventDefault`); `closest('a')` deckt einen künftigen einfachen `<a>` in
  // einer Zelle — als einzelne Mutation darum grün, beide zusammen sind gepinnt.
  const oeffneZeile = useCallback((e, id) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if (e.target instanceof Element && e.target.closest('a')) return
    const auswahl = typeof window.getSelection === 'function' ? String(window.getSelection() || '') : ''
    if (auswahl) return
    navigate(`/invoicing/${id}`)
  }, [navigate])

  // Das Sprungziel der Kachel „Open blocks“ (L2) — die Warnleiste desselben
  // Panels; `tabIndex={-1}` an ihrem Kasten macht sie zum Ankerziel.
  const warnleisteRef = useRef(null)
  const [warnungen, setWarnungen] = useState(null)
  const [warnungenFehler, setWarnungenFehler] = useState(null)
  // Welche Warnklasse aufgeklappt ist (S3): eine zur Zeit, wie eine Disclosure.
  const [offeneWarnung, setOffeneWarnung] = useState(null)

  const [liste, setListe] = useState(null)
  const [listeLaedt, setListeLaedt] = useState(true)
  const [listeFehler, setListeFehler] = useState(null)

  // ── Die Filter aus der URL lesen ─────────────────────────────────────────
  const filter = useMemo(() => {
    const aus = {}
    for (const schluessel of FILTER_SCHLUESSEL) {
      const wert = searchParams.get(schluessel)
      if (wert) aus[schluessel] = wert
    }
    const status = searchParams.getAll('status').filter(Boolean)
    return { ...aus, status }
  }, [searchParams])

  const seite = Math.max(1, Number(searchParams.get('page')) || 1)
  const hatFilter = FILTER_SCHLUESSEL.some((s) => searchParams.get(s))
    || filter.status.length > 0

  // ── Ein Filterwert in die URL schreiben (Seite zurück auf 1) ─────────────
  const setzeFilter = useCallback((schluessel, wert) => {
    const next = new URLSearchParams(searchParams)
    if (wert === '' || wert == null) next.delete(schluessel)
    else next.set(schluessel, String(wert))
    next.delete('page')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const setzeStatus = useCallback((werte) => {
    const next = new URLSearchParams(searchParams)
    next.delete('status')
    for (const wert of werte) next.append('status', wert)
    next.delete('page')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // 🔴 N10.2: der Seitenwechsel ist ein eigener Verlaufseintrag (push), kein
  //    `replace` — „Zurück“ führt auf die vorige Seite. Bis G7a stand hier
  //    `replace`; das war die Abweichung, die G7 behebt.
  const setzeSeite = useCallback((p) => {
    const next = new URLSearchParams(searchParams)
    if (p <= 1) next.delete('page')
    else next.set('page', String(p))
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const filterZuruecksetzen = useCallback(() => {
    // 🔴 Der Testbeleg-Schalter ist kein Filter — „Filter zurücksetzen" lässt ihn stehen.
    const next = new URLSearchParams()
    if (testbelege) next.set('testbelege', '1')
    setSearchParams(next, { replace: true })
  }, [setSearchParams, testbelege])

  // 🔴 R16 C2 (2026-11-01): der Leerzustand NENNT den Schalter — hier bietet er ihn
  // auch an. Dieselbe Form wie „Reset filters" darunter.
  // 🔴 S2: **keine** zweite Schreibstelle — der Knopf ruft `setzeTestbelege` der
  //    Schale (F2.7: EIN Schalter, EIN Zustand). Er setzt zusätzlich `page`
  //    zurück, das tut der Setter der Schale bereits.
  const schalteTestbelegeEin = useCallback(() => {
    setzeTestbelege(true)
  }, [setzeTestbelege])

  // ── Gedrosselte Textfilter: Suche, Land, Betrag ──────────────────────────
  const [qEingabe, setQEingabe] = useGedrosselterFilter('q', searchParams, setzeFilter)
  // 🔴 S3-NB1 (Z9): der Weg aus dem Begriff-Echo des Leerzustands — er leert
  //    NUR die Suche, nicht die uebrigen Filter (die stehen als Chips daneben).
  //    Das Eingabefeld wird mitgeleert, sonst stuende der alte Begriff im Feld,
  //    waehrend die Liste wieder alles zeigt.
  const sucheLeeren = useCallback(() => {
    setQEingabe('')
    setzeFilter('q', '')
  }, [setQEingabe, setzeFilter])
  const [landEingabe, setLandEingabe] = useGedrosselterFilter(
    'land', searchParams, setzeFilter, normalisiereLand)
  const [betragMinEingabe, setBetragMinEingabe, betragMinFehler] = useGedrosselterFilter(
    'betrag_min', searchParams, setzeFilter, normalisiereBetrag, pruefeBetrag)
  const [betragMaxEingabe, setBetragMaxEingabe, betragMaxFehler] = useGedrosselterFilter(
    'betrag_max', searchParams, setzeFilter, normalisiereBetrag, pruefeBetrag)

  // ── Z3: die Zeile der aktiven Filter (S3, §2.1) ───────────────────────────
  // 🔴 Die Chips sind eine ANZEIGE der URL, kein zweiter Zustand: jeder ✕ ruft
  //    denselben Setzer, den die Facette im Feld ruft. Dieselben Parameter,
  //    dieselbe URL, dieselbe Antwort (Uebergabe §9).
  // 🔴 S3-NB1 (Z10, Nachtrag 01.11.): es gibt hier bewusst KEINEN Chip fuer
  //    „Test invoices". Die Skizze §2.1 Z3 zeigt einen (`[Test invoices: hidden
  //    · default ✕]`), aber L3 (§1.3) und die Zeile `:549` nennen nur den
  //    SCHALTER, und der zeigt seinen Zustand schon selbst. Ein zweiter Ort
  //    waere eine zweite Anzeige desselben Schalters mit einem zweiten Weg,
  //    ihn zu aendern.
  const chips = useMemo(() => {
    const optionenLabel = (optionen, wert) => (
      (optionen.find((o) => o.value === wert) || {}).label || wert
    )
    const aus = []
    const dazu = (id, label, onRemove) => aus.push({ id, label, onRemove })
    if (qEingabe) dazu('q', `Search: ${qEingabe}`, () => setzeFilter('q', ''))
    if (filter.status.length > 0) {
      dazu('status', `Status: ${filter.status.map((s) => STATUS_LABEL[s] || s).join(', ')}`,
        () => setzeStatus([]))
    }
    if (filter.art) {
      dazu('art', `Type: ${optionenLabel(ART_OPTIONEN, filter.art)}`, () => setzeFilter('art', ''))
    }
    if (filter.kanal) {
      dazu('kanal', `Channel: ${optionenLabel(KANAL_OPTIONEN, filter.kanal)}`, () => setzeFilter('kanal', ''))
    }
    if (filter.steuerfall) {
      dazu('steuerfall', `Tax case: ${optionenLabel(STEUERFALL_OPTIONEN, filter.steuerfall)}`,
        () => setzeFilter('steuerfall', ''))
    }
    if (filter.land) dazu('land', `Country: ${filter.land}`, () => setzeFilter('land', ''))
    if (filter.von) dazu('von', `From: ${filter.von}`, () => setzeFilter('von', ''))
    if (filter.bis) dazu('bis', `To: ${filter.bis}`, () => setzeFilter('bis', ''))
    if (filter.betrag_min) dazu('betrag_min', `Amount from: ${filter.betrag_min}`, () => setzeFilter('betrag_min', ''))
    if (filter.betrag_max) dazu('betrag_max', `Amount to: ${filter.betrag_max}`, () => setzeFilter('betrag_max', ''))
    return aus
  }, [qEingabe, filter, setzeFilter, setzeStatus])

  // ── Laden ────────────────────────────────────────────────────────────────
  // 🔴 Die Kennzahlen lädt die SCHALE (`InvoiceList`): sie gelten für alle drei
  //    Reiter. Hier bleibt nur der Griff zum Auffrischen — der Refresh-Knopf der
  //    Liste nimmt die Kacheln mit, nicht nur die Zeilen.
  const ladeWarnungen = useCallback(() => {
    let abgemeldet = false
    api.get('/api/invoicing/admin/warnungen', {
      params: testbelege ? { testbelege: 1 } : {},
    })
      .then(({ data }) => { if (!abgemeldet) { setWarnungen(data); setWarnungenFehler(null) } })
      // 🔴 Kein stilles `catch`: eine leere Warnleiste sähe aus wie „geprüft,
      //    nichts offen“. Der Fehler bekommt einen eigenen Hinweis.
      .catch((e) => {
        if (abgemeldet) return
        setWarnungen(null)
        setWarnungenFehler(e?.response?.data?.error || e?.message || 'Unknown error')
      })
    return () => { abgemeldet = true }
  }, [testbelege])

  const ladeAlles = useCallback(() => {
    ladeKennzahlen()
    ladeWarnungen()
  }, [ladeKennzahlen, ladeWarnungen])

  // Ein Zähler, der die LISTE neu lädt: „Aktualisieren“ und „Erneut versuchen“
  // müssen die Liste mitnehmen, nicht nur die Kacheln.
  const [neuLaden, setNeuLaden] = useState(0)
  const allesNeuLaden = useCallback(() => {
    ladeAlles()
    setNeuLaden((n) => n + 1)
  }, [ladeAlles])

  // Warnungen einmal je Mount; der Aufräumer wird EINGESAMMELT und nicht
  // weggeworfen — ein verworfener Aufräumer setzte `abgemeldet` nie.
  useEffect(() => {
    const warnungenAb = ladeWarnungen()
    return () => { warnungenAb() }
  }, [ladeWarnungen])

  const listeAbfrage = useMemo(() => {
    const params = {}
    for (const schluessel of FILTER_SCHLUESSEL) {
      if (filter[schluessel]) params[schluessel] = filter[schluessel]
    }
    if (filter.status.length) params.status = filter.status
    if (testbelege) params.testbelege = 1
    if (seite > 1) params.page = seite
    params.per_page = PRO_SEITE
    return params
  }, [filter, seite, testbelege])

  useEffect(() => {
    let abgemeldet = false
    setListeLaedt(true)
    setListeFehler(null)
    api.get('/api/invoicing/admin/invoices', {
      params: listeAbfrage,
      // 🔴 `status` ist mehrfach. Ohne diesen Serialisierer schriebe axios
      //    `status[]=…`, und `request.args.getlist("status")` im Backend fände
      //    den Schlüssel nicht — der Filter liefe still ins Leere.
      paramsSerializer: { indexes: null },
    })
      .then(({ data }) => {
        if (abgemeldet) return
        setListe(data)
        // 🔴 Die Antwort nennt die Seite, die sie WIRKLICH geliefert hat — das
        //    Backend kappt `page` auf die letzte sinnvolle (G8). Weicht sie von
        //    der angefragten ab, zieht die URL nach; sonst zeigte ein geteilter
        //    Link `?page=99` eine Fußzeile, die zu keiner Zeile passt.
        const geliefert = Math.max(1, Number(data?.page) || 1)
        if (geliefert !== (listeAbfrage.page || 1)) {
          setSearchParams((vorher) => {
            const next = new URLSearchParams(vorher)
            if (geliefert <= 1) next.delete('page')
            else next.set('page', String(geliefert))
            return next
          }, { replace: true })
        }
      })
      .catch((e) => { if (!abgemeldet) setListeFehler(e?.response?.data?.error || e?.message || 'Unknown error') })
      .finally(() => { if (!abgemeldet) setListeLaedt(false) })
    return () => { abgemeldet = true }
  }, [listeAbfrage, neuLaden, setSearchParams])

  // ── Die Warnleiste ───────────────────────────────────────────────────────
  // Nur Klasse und Anzahl: die Beispiele des Backends sind interne
  // Rechnungs-IDs. Seit S5.5-F gäbe es ein Detail, auf das sie zeigen könnten —
  // die Verknüpfung ist ein eigener Schritt (`invoicing.md` §S5.5-F, offene Punkte).
  // „Lädt“ heisst: noch keine Antwort und kein Fehler. Ein Fehler hat seinen
  // eigenen Alert; ein Neuladen nach Erfolg lässt die alte Leiste stehen.
  const warnungenLaedt = warnungen === null && !warnungenFehler
  // 🔴 R16 W1: die Klasse `refund` führt auf ihren Reiter (`?tab=refunds`) — sonst
  // kann die Bedienerin die Fälle hinter der Zahl nicht finden. Der Wechsel läuft
  // über die Reiter-Achse der SCHALE (`zuRefunds`): sie legt einen Verlaufseintrag
  // an und setzt `page` zurück (N10.3). Ein Deep-Link je Beispiel gibt es nicht:
  // die `beispiele` sind interne Kennungen (Refund-IDs), keine Rechnungsnummern.

  const warnPillen = useMemo(() => {
    const klassen = warnungen?.klassen || []
    // 🔴 W5 (Orchestrator-Entscheid 12.10.2026): die Klasse `stichtag` erscheint im Modus
    // `off` ab 1 — dort ist jede unmarkierte Zeile ein Signal für einen zu alten Stichtag.
    // In `pilot`/`live` läuft der Verbraucher (Takt ~2 min), ein kleiner Rückstand ist dort
    // normal; erst ab `STICHTAG_SCHWELLE_AKTIV` ist der Stichtag verdächtig. Ist der Modus
    // NICHT gelesen, gilt die `off`-Schwelle — mehr zeigen ist billiger als weniger.
    const modusIssue = kennzahlen?.modus?.issue
    const stichtagAktiv = modusIssue === 'live' || modusIssue === 'pilot'
    return klassen
      .filter((k) => {
        const n = k?.anzahl || 0
        if (k?.klasse === 'stichtag') {
          return stichtagAktiv ? n >= STICHTAG_SCHWELLE_AKTIV : n > 0
        }
        return n > 0
      })
      .map((k) => ({
        klasse: k.klasse,
        anzahl: k.anzahl,
        beispiele: Array.isArray(k.beispiele) ? k.beispiele : [],
        schwere: WARN_SCHWERE[k.klasse] || 'info',
        rang: WARN_RANG[k.klasse] || 0,
        titel: k.klasse === 'stichtag'
          ? (stichtagAktiv ? 'Issuance backlog at the cut-off' : WARN_TITEL.stichtag)
          : (WARN_TITEL[k.klasse] || k.klasse),
        // 🔴 Der Text ist modus-abhängig UND ehrlich (W1–W3): die Zahl ist eine Obergrenze
        // des VERBRAUCHERS — der Sweeper zieht Bestellungen aus Shopify und kann weitere
        // versendete Bestellungen hinzufügen. Zeilen sind keine Bestellungen, und eine
        // Zeile nach dem Stichtag kann eine Sendung davor tragen: deshalb „up to this many"
        // und nicht „N invoices".
        hinweis: k.klasse === 'stichtag'
          ? (stichtagAktiv
            ? 'This many rows from the last 14 days are waiting at the cut-off; the consumer may be behind, or the cut-off date is stale.'
            : 'Switching on would issue up to this many; the sweeper can add further shipped orders; set the cut-off date to the go-live day before switching on.')
          : null,
      }))
      // Schwerste zuerst, innerhalb der Bande der Rang (stichtag vor den uebrigen
      // Warnungen, dispute vor refund).
      .sort((a, b) => (SCHWERE_RANG[b.schwere] || 1) - (SCHWERE_RANG[a.schwere] || 1)
        || b.rang - a.rang)
  }, [warnungen, kennzahlen])

  // ── Der Ausstellungsmodus (für Leerzustand und `stichtag`-Schwelle) ──────
  // Die Kacheln selbst stehen in der Schale; hier braucht nur der Leerzustand
  // den Modus, und die Warnleiste ihre Schwelle.
  const modus = kennzahlen?.modus || {}

  // ── Der Leerzustand: der Modus nur, wenn er GELESEN wurde ────────────────
  const modusGelesen = typeof modus.issue === 'string' && modus.issue !== ''
  let leerText
  // 🔴 Welle 3 PR 4 (§4.1): der Env-Name gehoert in den Tooltip, nicht in den
  // Satz. Die `german-words`-Ausnahme an der alten Zeile ist damit entfallen —
  // der Satz ist englisch, der Env-Name steht in `leerTitle`.
  let leerTitle
  if (hatFilter) {
    leerText = 'No document matches the current selection.'
  } else if (!modusGelesen) {
    leerText = kennzahlenLaedt
      ? 'The issuance mode is still being read.'
      : 'The issuance mode could not be read.'
  } else if (modus.issue === 'off') {
    leerText = `Invoices are issued once issuance is enabled (currently: ${modusText(modus.issue)}).`
    leerTitle = 'INVOICING_ISSUE_MODE=off'
  } else if (!testbelege) {
    // 🔴 Go-live-Morgen (F2.7): die Tagesansicht zeigt die produktiven Rechnungen.
    // Ist sie leer, weil nur Testbelege existieren, sagt der Satz das und nennt den
    // Schalter — statt einer leeren Tabelle ohne Erklärung.
    leerText = 'Test invoices are hidden by default — switch on “Show test invoices” to include them.'
  } else {
    leerText = `Issuance is active (mode: ${modusText(modus.issue)}). Nothing has been issued yet.`
  }

  // 🔴 R16 §2/§5 (2026-11-01): EIN Prädikat für Titel, Rumpf und Knopf. Nur in
  // diesem Zustand sagt der Titel „No productive invoices yet." — sonst behauptete
  // er neben „Test invoices are hidden", es gebe keine Rechnungen, obwohl es sie
  // gibt. Und nur hier erklärt der Rumpf den Knopf; in den anderen Zweigen
  // (Modus unlesbar / `off`) stand der Knopf ohne den Satz, der ihn erklärt.
  const leerNenntTestbelege = !hatFilter && modusGelesen
    && modus.issue !== 'off' && !testbelege

  const zeilen = liste?.items || []

  // ── Aufklappen der Gruppen (B2) ──────────────────────────────────────────
  // 🔴 Der Gruppenknoten trägt `gruppe` (stabil) als Schlüssel. Eine Gruppe ist
  //    VORGABE offen, wenn ein **Kind** `trifft` trägt — die Suche nach einer
  //    Korrekturnummer klappt ihre Gruppe damit von selbst auf (Owner). Ein
  //    ausdrückliches Klicken überschreibt die Vorgabe je Gruppe. Die
  //    Gruppierung selbst baut die Oberfläche **nicht** nach — sie liest
  //    `kopf`/`kinder` (Review Focus 5, `gruppen.py` ist die eine Stelle).
  const [klappen, setKlappen] = useState({})
  const istOffen = useCallback((g) => (
    klappen[g.gruppe] ?? (g.kinder || []).some((k) => k.trifft)
  ), [klappen])
  const schalte = useCallback((g) => setKlappen((v) => ({
    ...v,
    [g.gruppe]: !(v[g.gruppe] ?? (g.kinder || []).some((k) => k.trifft)),
  })), [])
  // Skeletons NUR beim ersten Laden; danach bleiben die alten Zeilen stehen.
  const ersterLadevorgang = listeLaedt && liste === null && !listeFehler
  // 🔴 S3-NB1 (Z9, Nachtrag 01.11.): `sperre` = der ERSTE Aufbau ist
  //    gescheitert, es gab noch nie eine Antwort. `liste === null` sagt
  //    dasselbe wie „nie geladen": der Zustand entsteht nur vor dem ersten
  //    Erfolg, und ein spaeterer Fehler laesst `liste` stehen. Nur dann werden
  //    Suche, Facetten und „Filters" gesperrt — ueber einer geladenen Liste
  //    bleibt alles bedienbar, sonst waere ein misslungener Nachladeversuch
  //    ein Bedienverbot auf einer Flaeche, die Daten zeigt.
  //
  // 🔴 R15 B4 (Nachbesserung 2): „bedienbar" ist die GANZE Zusage, die dieser
  //    Zustand traegt — nicht „nichts aendert sich". Was ein Nachladefehler
  //    ueber einer geladenen Liste tatsaechlich tut, steht drei Zeilen weiter
  //    unten im Rumpf und ist hier bewusst NICHT beschoenigt:
  //      · die BEDIENELEMENTE (Suche, drei Facetten, „Filters") bleiben
  //        bedienbar — dafuer ist `sperre` da;
  //      · die geladenen ZEILEN weichen der Fehlerzeile (`listeFehler &&
  //        <TableRow>…` und `!listeFehler && zeilen.map(…)`);
  //      · die FUSSZEILE bleibt stehen und liest weiter `liste?.total`, sie
  //        zeigt also den ALTEN Bestand, waehrend der Rumpf den Fehler nennt.
  //    Gegenueber der Basis ist das nicht schlechter (dort verschwand mit
  //    `zeigeErgebnis` der ganze Block), aber es ist auch keine Ruhe: Kopf und
  //    Fuss stehen, der Rumpf ist Fehler. `InvoiceList.test.jsx` haelt genau
  //    diese drei Punkte fest („Nachladefehler").
  const sperre = liste === null && Boolean(listeFehler)

  // ── Die Filterfelder ─────────────────────────────────────────────────────
  // 🔴 S3 (§2.1): DREI Facetten inline — Status · Type · Tax case. `Channel` ist
  //    die vierte gewesen und wandert ins Blatt („der Rest im Popover"); vier
  //    Facetten in Z1 sind der Grund, warum die Zeile bei 1100 px kippte.
  //    🔴 S3-NB1 (§2.1, Nachtrag 01.11.): die drei tragen eine SCHMALERE
  //    Mindestbreite als die Baustein-Vorgaben (200 bzw. 160). Zusammen sparen
  //    sie 140 px — genau die Luft, die dem Suchfeld bei 1440 fehlte. Die
  //    Bausteine bleiben unberuehrt (`sx` ist dort vorgesehen).
  const facetten = (
    <>
      <BottomSheetMultiSelect
        label="Status"
        value={filter.status}
        onChange={setzeStatus}
        options={STATUS_OPTIONEN}
        disabled={sperre}
        sx={{ minWidth: 140 }}
      />
      <BottomSheetSelect
        label="Type"
        value={filter.art || ''}
        onChange={(wert) => setzeFilter('art', wert)}
        options={ART_OPTIONEN}
        clearable
        disabled={sperre}
        sx={{ minWidth: 120 }}
      />
      <BottomSheetSelect
        label="Tax case"
        tooltip="Steuerfall"
        value={filter.steuerfall || ''}
        onChange={(wert) => setzeFilter('steuerfall', wert)}
        options={STEUERFALL_OPTIONEN}
        clearable
        disabled={sperre}
        sx={{ minWidth: 120 }}
      />
    </>
  )

  // Die Kriterien des Blattes: kompakt (und auf dem Telefon) ALLE, sonst nur die
  // eine, die nicht mehr inline steht. 🔴 Ohne diesen Zweig waeren Status, Type
  // und Tax case unter 1360 px unerreichbar — die Facetten sind dort ausgeblendet
  // (`ListToolbar`), und das Blatt traege nur `Channel`.
  const blattKriterien = [
    {
      key: 'status',
      label: 'Status',
      multiple: true,
      value: filter.status,
      onChange: setzeStatus,
      options: STATUS_OPTIONEN,
    },
    {
      key: 'art',
      label: 'Type',
      clearable: true,
      value: filter.art || '',
      onChange: (wert) => setzeFilter('art', wert),
      options: ART_OPTIONEN,
    },
    {
      key: 'steuerfall',
      label: 'Tax case',
      clearable: true,
      value: filter.steuerfall || '',
      onChange: (wert) => setzeFilter('steuerfall', wert),
      options: STEUERFALL_OPTIONEN,
    },
    {
      key: 'kanal',
      label: 'Channel',
      clearable: true,
      value: filter.kanal || '',
      onChange: (wert) => setzeFilter('kanal', wert),
      options: KANAL_OPTIONEN,
    },
  ]
  const blattKriterienAktiv = istKompakt
    ? blattKriterien
    : blattKriterien.filter((k) => k.key === 'kanal')

  // 🔴 S3-NB1 (Z8, Nachtrag 01.11.): die Felder standen als FRAGMENT
  //    nebeneinander — ohne Abstand, und bei 390 px ueberlappten ihre
  //    Beschriftungen (Foto `390_liste-filter-blatt_klick`). Ein `Stack` mit
  //    `SP.card` gibt ihnen denselben vertikalen Rhythmus wie das Blatt selbst.
  const sheetFelder = (
    <Stack spacing={SP.card} sx={{ mt: SP.card }}>
      <TextField
        size="small"
        fullWidth
        label="Country"
        value={landEingabe}
        onChange={(e) => setLandEingabe(e.target.value)}
        helperText="ISO code, e.g. DE"
      />
      <Stack direction="row" spacing={SP.tight}>
        <TextField
          size="small"
          fullWidth
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={filter.von || ''}
          onChange={(e) => setzeFilter('von', e.target.value)}
        />
        <TextField
          size="small"
          fullWidth
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={filter.bis || ''}
          onChange={(e) => setzeFilter('bis', e.target.value)}
        />
      </Stack>
      <Stack direction="row" spacing={SP.tight} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          label="Amount from"
          inputProps={{ inputMode: 'decimal' }}
          value={betragMinEingabe}
          onChange={(e) => setBetragMinEingabe(e.target.value)}
          error={Boolean(betragMinFehler)}
          helperText={betragMinFehler || ' '}
        />
        <TextField
          size="small"
          fullWidth
          label="Amount to"
          inputProps={{ inputMode: 'decimal' }}
          value={betragMaxEingabe}
          onChange={(e) => setBetragMaxEingabe(e.target.value)}
          error={Boolean(betragMaxFehler)}
          helperText={betragMaxFehler || ' '}
        />
      </Stack>
    </Stack>
  )

  // Wie viele Filter hinter dem Öffner aktiv sind (Desktop: die Felder; Mobil: alle).
  const sheetAktiv = SHEET_SCHLUESSEL.filter((s) => searchParams.get(s)).length
  // Was im BLATT steht und einen Wert hat — je Breite verschieden, weil die drei
  // Facetten nur oberhalb 1360 px inline stehen (`blattKriterienAktiv`).
  const blattAktiv = sheetAktiv + blattKriterienAktiv.filter((k) => (
    k.multiple ? (k.value || []).length > 0 : Boolean(k.value)
  )).length

  // ── Z0 — die KPI-Reihe (S2) ─────────────────────────────────────────────
  // Reihenfolge nach L2: **Open blocks · Without number · Created today ·
  // Mailed today · Corrected today**. „Corrected today“ entfällt NICHT — es
  // ist die fünfte Kachel und liegt mobil hinter „All metrics“ (`KpiRow`,
  // `maxMobile` bleibt bei der Vorgabe 4: die verborgenen Kacheln tragen
  // weder Handlung noch Statusfarbe, `KpiRow.jsx:135-145`).
  // `abgewiesen` steht als Hinweis an „Mailed today“ (die Ablehnungen gehören
  // zum Versand). `refund_offen` steht NICHT als Kachel-Hinweis: es ist
  // dieselbe Zahl wie die Warnklasse `refund` und gehörte nicht zur Familie
  // der Sperren.
  //
  // 🔴 Regel 22 §3: ohne gelesene Kennzahlen gibt es KEINE Hinweiszahl. Ein
  //    `?? 0` hätte unter einem „—“ eine sichere Null behauptet.
  const zahlOderNichts = (wert) => (Number.isFinite(wert) ? wert : undefined)
  const abgewiesen = zahlOderNichts(kennzahlen?.abgewiesen)
  // 🔴 L2 (§1.2): „Open blocks“ ist ein **Sprung in die Warnleiste** desselben
  //    Panels — ein Weg zum Beleg, KEIN Filter (einen Filter „offene Sperre“
  //    gibt es serverseitig nicht). „Without number“ bleibt bewusst ohne Weg:
  //    ein clientseitiger Filter über die geladene Seite behauptete „0“, weil
  //    die Liste serverseitig auf 50 je Seite paginiert ist.
  //    `offene_sperren` ist KEINE Summe der Warnklassen (a2, §6 Nr. 7) — die
  //    Kachel behauptet keine Zahl, die sie nicht hat.
  const offeneSperren = zahlOderNichts(kennzahlen?.offene_sperren)
  const springeZurWarnleiste = useCallback(() => springeZu(warnleisteRef.current), [])
  const kacheln = [
    {
      label: 'Open blocks',
      value: kennzahlen?.offene_sperren,
      ...(offeneSperren > 0 && {
        onClick: springeZurWarnleiste,
        // 🔴 R16 B1 (2026-11-01): ein gesetztes `aria-label` ERSETZT den Inhalt
        //    (`StatTile.jsx:346` ist ein `||`, kein Zusammenbau) — der
        //    Screenreader hörte „Open blocks — jump to the warnings" und die
        //    Zahl nie. Sie steht jetzt IM Namen, mit demselben Wert, den die
        //    Kachel zeigt.
        //    🔴 Der Name traegt den Wert hier ABSICHTLICH, die Nachbarn nicht:
        //    `StatTile` baut `Label: Wert` nur fuer nicht-numerische Werte
        //    (`StatTile.jsx:346-348`, `isNumeric` `:223`) — bei Zahlen traegt
        //    der Name nur das Etikett. Diese Kachel ist ein Knopf; ohne den Wert
        //    im Namen hoert man das Ziel, aber nicht die Zahl.
        //    🔴 „Unbekannt" kann hier nicht auftreten: ohne gelesene Zahl ist
        //    `offeneSperren` `undefined`, die Kachel dann KEIN Knopf
        //    (`offeneSperren > 0` ist falsch) und trägt gar kein `aria-label`.
        ariaLabel: `Open blocks: ${offeneSperren} — jump to the warnings`,
      }),
    },
    { label: 'Without number', value: kennzahlen?.ohne_nummer },
    {
      label: 'Created today',
      value: kennzahlen?.erzeugt,
      hint: kennzahlen?.tag ? formatBerlinDate(kennzahlen.tag, { dateOnly: true }) : undefined,
      mitHinweis: true,
    },
    {
      label: 'Mailed today',
      value: kennzahlen?.versandt,
      hint: abgewiesen === undefined ? undefined : `${abgewiesen} rejected`,
      mitHinweis: true,
    },
    { label: 'Corrected today', value: kennzahlen?.korrigiert },
  ]

  // 🔴 S3-NB1 (Z4, Nachtrag 01.11.): der Testbelege-Schalter steht auf dem
  //    Telefon NICHT in Z1, sondern als erste Zeile in Z2 ueber der Warnleiste.
  //    Gemessen bei 390 px: „Filters" ≈ 100 px, der Schalter mit sichtbarem Text
  //    ≈ 170 px — die Suchpille verschwand aus der Zeile (Probe C.1). Der
  //    Schalter bleibt derselbe Knoten und dieselbe Schreibstelle
  //    (`setzeTestbelege`); er wechselt nur den Ort.
  const testbelegeSchalter = (
    <FormControlLabel
      control={(
        <Switch
          size="small"
          checked={testbelege}
          onChange={(e) => setzeTestbelege(e.target.checked)}
          inputProps={{ 'aria-label': 'Show test invoices' }}
        />
      )}
      label="Show test invoices"
      // 🔴 R16 W2 (Nachbesserung 2): MUI setzt an `FormControlLabel` mit
      //    `labelPlacement="end"` ein `margin-left: -11px`, damit die
      //    Schalterbahn optisch auf der Textkante sitzt. In Z1 (ab `sm`) faellt
      //    das im Flex-Band nicht auf; als eigene Zeile unter `sm` haengt der
      //    Schalter damit sichtbar links aus dem 16-px-Raster (Fotos
      //    `390_liste-grund`, `390_dunkel_liste-grund`). Nur dort zurueck auf 0.
      sx={{ ml: { xs: 0 } }}
    />
  )

  return (
    <>
      {/* ── Z0: die KPI-Reihe — IM Panel (S2) ──────────────────────────────
          🔴 Die Marke `liste-kpi` liegt seit S2 INNERHALB von `liste-panel`;
          die neue `/invoicing`-Zusicherung prüft Containment, nicht
          `top`-Ordnung (§4.2). Auf Operations und Refund decisions gibt es
          diese Fläche nicht. */}
      <Box data-testid="liste-kpi" sx={{ mb: SP.card }}>
        {kennzahlenFehler && (
          <Alert
            severity="warning"
            sx={{ mb: SP.card }}
            action={<Button color="inherit" size="small" onClick={ladeKennzahlen}>Reload metrics</Button>}
          >
            Metrics could not be loaded: {kennzahlenFehler}
          </Alert>
        )}
        <KpiRow>
          {kacheln.map((k) => (
            <KpiCell key={k.label}>
              <StatTile
                label={k.label}
                value={k.value}
                hint={k.hint}
                onClick={k.onClick}
                ariaLabel={k.ariaLabel}
                loading={kennzahlenLaedt}
                reserveHint={Boolean(k.mitHinweis)}
              />
            </KpiCell>
          ))}
        </KpiRow>
      </Box>

      {/* ── Die Warnungen konnten nicht geladen werden — eigener Hinweis ──── */}
      {warnungenFehler && (
        <Alert
          severity="warning"
          sx={{ mb: SP.card }}
          action={<Button color="inherit" size="small" onClick={ladeWarnungen}>Reload warnings</Button>}
        >
          The warnings could not be loaded — whether anything is open is unknown. ({warnungenFehler})
        </Alert>
      )}

      {/* ── Z1 / Z2 / Z3 — die Leiste (S3, G7b) ───────────────────────────
          🔴 Die Marke heisst seit S3 `liste-z1` (vorher
          `rechnungen-filterzeile`, §4.3 Nr. 2) und kommt vom Baustein.
          `kompakt` gilt unter 1360 px (`Z1_BREIT_AB`): dann stehen die drei Facetten im Blatt
          und der Zähler in Z3 (§2.1 „900“).
          🔴 `quickFilters` ist Z2 und traegt hier die WARNLEISTE — sie ist die
          Pillenzeile zwischen Z1 und Z3 (§2.1); einen zweiten Slot zwischen
          den beiden Zonen gibt der Baustein nicht.
          🔴 `z1Extra` traegt den Testbelege-Schalter: er ist kein
          Blatt-Kriterium (er gehoert sichtbar in die Leiste, L3) und keine
          Facette (die blendet unter `sm` aus). 🔴 S3-NB1 (Z4, Nachtrag 01.11.):
          auf dem TELEFON steht er als erste Zeile in Z2 ueber der Warnleiste —
          in Z1 verdraengte er bei 390 px die Suchpille (Probe C.1). L3 bleibt
          erfuellt: er steht in der Leiste jeder betroffenen Liste, nur eine
          Zone tiefer. */}
      <ListToolbar
        search={{
          value: qEingabe,
          onChange: setQEingabe,
          placeholder: 'Number or order name…',
          // 🔴 S3-NB1 (§2.1, Nachtrag 01.11.): `SearchTrigger` zeichnet sein
          //    `TextField` mit `sx={{ minWidth: 220, ...sx }}`. Ohne eigenes `sx`
          //    konnte das Feld nicht unter 220 px schrumpfen und ragte aus seinem
          //    (schrumpfbaren) Kasten heraus — bei 1100 und 1440 lag es damit
          //    UNTER der Facette „Status" (Fotos `1100_liste-grund`,
          //    `1440_liste-leer`). `scrollWidth` sah davon nichts, die
          //    Ueberlaufmessung meldete „ueber: 0" (Schein-Gruen).
          //    `minWidth: 0` hebt den Boden auf, `flex: 1` fuellt den Kasten.
          sx: { flex: 1, minWidth: 0 },
          // 🔴 Hausform fuer die Rolle des Feldes (`grep -rn "inputRole:
          //    'searchbox'" frontend/src`): P5/G0.12. Ohne sie traegt das Feld
          //    keine `searchbox`-Rolle, und die Leistenprobe kann es nicht
          //    rollenbasiert finden.
          inputRole: 'searchbox',
          // 🔴 Z9: gesperrt, solange der erste Aufbau gescheitert ist.
          disabled: sperre,
        }}
        facets={facetten}
        moreFilters={{
          label: 'Filters',
          title: 'Filters',
          activeCount: blattAktiv,
          criteria: blattKriterienAktiv,
          children: sheetFelder,
          disabled: sperre,
        }}
        count={{
          n: liste?.total,
          noun: 'orders',
          loading: listeLaedt,
          // 🔴 Regel 22 §3: ist der Abruf gescheitert, ist die Zahl UNBEKANNT —
          //    nicht die des letzten Erfolgslaufs und nicht 0.
          unknown: liste === null && Boolean(listeFehler),
        }}
        // 🔴 Genau EIN Refresh, am ENDE von Z1 (W-12, Akzeptanz 7). Bis S2 stand
        //    er in einer eigenen Zeile darueber (G7a-B1/W2) — die Notloesung,
        //    die §4.3 Nr. 1 ersetzt. Der Reiter Operations meldet seinen eigenen
        //    Refresh ab `lg` ueber `meldeKopf` an den Kopf der Schale, darunter in
        //    `liste-betrieb-aktionen` (Symbol „Refresh invoice operations"); der Refresh der Liste heisst
        //    `Refresh invoice list`. `firstLoad`: im Erstaufbau stehen Skelette
        //    (`rechnungen-laden`), der Knopf dreht sich dann nicht mit — ein
        //    zweiter Ladehinweis fuer denselben Abruf waere genau das, was
        //    Anti-Pattern 11 verbietet.
        refresh={(
          <RefreshButton
            ariaLabel="Refresh invoice list"
            loading={listeLaedt}
            firstLoad={ersterLadevorgang}
            onClick={allesNeuLaden}
          />
        )}
        kompakt={istKompakt}
        z1Extra={istSchmal ? undefined : testbelegeSchalter}
        quickFilters={(
          <>
            {istSchmal && (
              <Box data-testid="testbelege-schalter-mobil" sx={{ mb: SP.tight }}>
                {testbelegeSchalter}
              </Box>
            )}
            <Warnleiste
              leisteRef={warnleisteRef}
              pillen={warnPillen}
              laedt={warnungenLaedt}
              offene={offeneWarnung}
              setOffene={setOffeneWarnung}
              zuRefunds={zuRefunds}
              zuBetrieb={zuBetrieb}
              kompakt={istKompakt}
            />
          </>
        )}
        query={{ chips, clearAll: filterZuruecksetzen }}
      >
        <>
        {/* ── Erstes Laden: das Geruest in SPALTENFORM (Akzeptanz §2.1) ─────
            🔴 Anti-Pattern 11: kein Spinner. Beim Neuladen bleiben die alten
            Zeilen stehen und nur die duenne Leiste zeigt die Arbeit an. */}
        {ersterLadevorgang && (
          <Paper elevation={0} sx={PANEL_SX} data-testid="rechnungen-laden" aria-busy="true">
            <Stack spacing={SP.tight}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} data-testid="rechnungen-skelett" variant="rounded" height={36} />
              ))}
            </Stack>
          </Paper>
        )}

        {/* ── Das Ergebnis — beim Neuladen bleibt es stehen ───────────────── */}
        {!ersterLadevorgang && (
          <Box data-testid="rechnungen-ergebnis" aria-busy={listeLaedt ? 'true' : 'false'}>
            {listeLaedt && (
              <LinearProgress aria-label="List is updating" sx={{ mb: SP.chip }} />
            )}

            {/* 🔴 Der Tabellenkopf bleibt im LEER- und FEHLERFALL stehen
                (Akzeptanz 6): die Spalten erklaeren die Flaeche, auch wenn
                keine Zeile da ist. Auf dem Telefon gibt es keinen Kopf — dort
                traegt die Karte die Beschriftungen. */}
            {!istSchmal && (
              <Paper elevation={0} sx={{ ...PANEL_SX, p: 0, overflow: 'hidden', mb: SP.chip }}>
                <TableViewport footerPadPx={0} sx={{ pb: 0 }}>
                  <Table size="small" data-testid="liste-tabelle">
                    <TableHead>
                      <TableRow>
                        <TableCell>Number</TableCell>
                        {/* 🔴 Der Glossar-Träger der Fläche (R15 B3, R16 N1):
                            mit dem Wegfall der Spalte „Tax case" war der
                            deutsche Begriff nur noch per Maus/Touch erreichbar
                            (`Fachbegriff … fokussierbar={false}` in den
                            Wert-Zellen). Der Kopf „Customer" traegt das
                            Glossar jetzt — er ist der EINE fokussierbare
                            Tab-Stopp dieser Tabelle, und die Metazeile, um
                            die es geht, haengt an genau dieser Spalte. */}
                        <TableCell>
                          <Fachbegriff label="Customer" begriff={KUNDEN_GLOSSAR} />
                        </TableCell>
                        {/* 🔴 S3-NB1 (Z6): unterhalb 1360 px fuenf Spalten —
                            `Date` steht dann in der Kunden-Metazeile. */}
                        {!istKompakt && <TableCell>Date</TableCell>}
                        <TableCell align="right">Gross</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Throughput</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listeFehler && (
                        <TableRow>
                          <TableCell colSpan={istKompakt ? 5 : 6}>
                            <EmptyState
                              reason="error"
                              message="The invoice list could not be loaded."
                              detail={listeFehler}
                              onRetry={allesNeuLaden}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {!listeFehler && zeilen.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={istKompakt ? 5 : 6}>
                            <Leerzustand
                              hatFilter={hatFilter}
                              leerTitle={leerTitle}
                              leerText={leerText}
                              leerNenntTestbelege={leerNenntTestbelege}
                              filterZuruecksetzen={filterZuruecksetzen}
                              schalteTestbelegeEin={schalteTestbelegeEin}
                              q={filter.q || ''}
                              sucheLeeren={sucheLeeren}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {!listeFehler && zeilen.map((g) => {
                        const kinder = g.kinder || []
                        const offen = istOffen(g)
                        // 🔴 R15-B1/R16-B1: die **Korrekturen** zählen, nicht alle
                        // Kinder. `kopf.korrekturen` kommt aus `gruppen.py:173`;
                        // `kinder.length` zählte eine zweite Rechnung (RF1) mit.
                        const hinweis = gruppeHinweis(g.kopf.korrekturen)
                        // Die Restsumme steht seit S3 in der GELD-Spalte (§2.1),
                        // nicht mehr im Gruppenhinweis — sonst stuende sie zweimal.
                        const rest = g.kopf.korrekturen > 0 && g.kopf.rest != null
                          ? zahl(g.kopf.rest) : null
                        const oeffne = g.kopf.id != null
                          ? (e) => oeffneZeile(e, g.kopf.id) : undefined
                        return (
                          <Fragment key={g.gruppe}>
                            {/* 🔴 Zeiger + Kontur: die Zeile öffnet das Detail
                                (S5.5-F). Der echte Link steckt in der Nummer —
                                Tastatur und neuer Tab. KEIN MUI-`hover`: dessen
                                Hintergrund legt sich unter die durchscheinende
                                Pill-Füllung (Gate S5.5-F, R16-1). */}
                            <TableRow
                              onClick={oeffne}
                              sx={{ cursor: oeffne ? 'pointer' : 'default', '&:hover > .MuiTableCell-root': { borderBottomColor: 'primary.main' } }}
                            >
                              <TableCell sx={{ fontSize: FS.sm }}>
                                <Stack direction="row" alignItems="flex-start" spacing={SP.chip}>
                                  {kinder.length > 0 ? (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); schalte(g) }}
                                      aria-label={offen ? 'Collapse corrections' : 'Expand corrections'}
                                      aria-expanded={offen}
                                      aria-controls={offen ? kinder.map(kinderId).join(' ') : undefined}
                                    >
                                      {offen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                    </IconButton>
                                  ) : (
                                    <Box sx={{ width: AUFKLAPP_BREITE_DESKTOP, flexShrink: 0 }} />
                                  )}
                                  <Box>
                                    {g.kopf.id != null ? (
                                      <Link
                                        component={RouterLink}
                                        to={`/invoicing/${g.kopf.id}`}
                                        aria-label={g.kopf.nummer ? undefined : `Entry without number (ID ${g.kopf.id})`}
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {kopfText(g.kopf, g.bezug)}
                                      </Link>
                                    ) : (
                                      <Typography component="span" sx={{ whiteSpace: 'nowrap' }}>{kopfText(g.kopf, g.bezug)}</Typography>
                                    )}
                                    {g.kopf.trifft && (
                                      <Pill tone="info" bordered nowrap>Match</Pill>
                                    )}
                                    {/* S3b (2026-11-03): die Shop-Bestellnummer als
                                        zweite Zeile — sichtbar, nicht im Tooltip
                                        (Lehre 4). `nowrap` traegt den NAMEN, der
                                        Zwischenraum bleibt aussen und darf brechen (Lehre 5).
                                        Ohne Bestellbezug steht hier NICHTS — keine leere Zeile,
                                        kein Strich; bewusst NICHT `bestellzeile` (liefert `—`). */}
                                    {g.kopf.bestellung && (
                                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        Order{' '}
                                        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{g.kopf.bestellung}</Box>
                                      </Typography>
                                    )}
                                    {gruppeMarke(g) && (
                                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        {gruppeMarke(g)}
                                      </Typography>
                                    )}
                                    {hinweis && (
                                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        {hinweis}
                                      </Typography>
                                    )}
                                  </Box>
                                </Stack>
                              </TableCell>
                              <KundenZelle z={g.kopf} mitDatum={istKompakt} />
                              {!istKompakt && <DatumZelle z={g.kopf} />}
                              <BetragZelle z={g.kopf} rest={rest} />
                              <TableCell><StatusPill status={g.kopf.status} /></TableCell>
                              <ThroughputZelle z={g.kopf} />
                            </TableRow>
                            {offen && kinder.map((k) => (
                              <TableRow
                                key={k.id}
                                id={kinderId(k)}
                                data-testid="gruppe-kind-zeile"
                                onClick={(e) => oeffneZeile(e, k.id)}
                                sx={{
                                  cursor: 'pointer',
                                  // 🔴 Getragen wird die Zugehörigkeit von der
                                  // EINRÜCKUNG (Nummer bei 74 px statt 32). Die
                                  // Fläche STÜTZT sie — gemessen 1,046:1 gegen das
                                  // Papier (hell) bzw. 1,22:1 (dunkel), weit unter
                                  // den 3:1, die WCAG 1.4.11 verlangte, wenn sie
                                  // allein trüge. `background.default` ist der eine
                                  // neutrale Ton unter `background.paper` — bewusst
                                  // NICHT `action.hover`.
                                  '& > .MuiTableCell-root': { bgcolor: 'background.default' },
                                  '&:hover > .MuiTableCell-root': { borderBottomColor: 'primary.main' },
                                }}
                              >
                                <TableCell sx={{ fontSize: FS.sm }}>
                                  <Stack direction="row" alignItems="flex-start" spacing={SP.chip}>
                                    {/* Flucht (R16 W1): derselbe 30-px-Platz wie der
                                        Aufklapp-Knopf des Kopfes. Gemessen 2026-10-14
                                        stand die Kindnummer OHNE ihn 18 px LINKS der
                                        Kopfnummer. */}
                                    <Box sx={{ width: AUFKLAPP_BREITE_DESKTOP, flexShrink: 0 }} />
                                    <Box sx={{ pl: SP.md }}>
                                      <Link
                                        component={RouterLink}
                                        to={`/invoicing/${k.id}`}
                                        aria-label={k.nummer ? undefined : `Entry without number (ID ${k.id})`}
                                        sx={{ whiteSpace: 'nowrap' }}
                                      >
                                        {k.nummer || 'without number'}
                                      </Link>
                                      {k.trifft && (
                                        <Pill tone="info" bordered nowrap>Match</Pill>
                                      )}
                                      {/* S3b: das Kind nennt seine Bestellnummer NUR,
                                          wenn sie von der des Kopfes abweicht — sonst
                                          stuende dieselbe Nummer zweimal untereinander. */}
                                      {k.bestellung && k.bestellung !== g.kopf.bestellung && (
                                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                          Order{' '}
                                          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{k.bestellung}</Box>
                                        </Typography>
                                      )}
                                      {bezugText(k.beleg_bezug) && (
                                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                          {bezugText(k.beleg_bezug)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Stack>
                                </TableCell>
                                <KundenZelle z={k} mitDatum={istKompakt} />
                                {!istKompakt && <DatumZelle z={k} />}
                                <BetragZelle z={k} rest={null} />
                                <TableCell><StatusPill status={k.status} /></TableCell>
                                <ThroughputZelle z={k} />
                              </TableRow>
                            ))}
                          </Fragment>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableViewport>
              </Paper>
            )}

            {/* ── Die Kartenliste (390) — dieselben Felder, dieselbe Ordnung ──
                🔴 `MobileRecordCard`: der TITEL ist der Weg in den Datensatz,
                die Nummer wird nie gekuerzt, hoechstens EINE Pille je Zeile
                steht neben dem Titel (Lehre 3), alles andere in `meta`. */}
            {istSchmal && (
              <Stack spacing={SP.tight}>
                {listeFehler && (
                  <EmptyState
                    reason="error"
                    message="The invoice list could not be loaded."
                    detail={listeFehler}
                    onRetry={allesNeuLaden}
                  />
                )}
                {!listeFehler && zeilen.length === 0 && (
                  <Leerzustand
                    hatFilter={hatFilter}
                    leerTitle={leerTitle}
                    leerText={leerText}
                    leerNenntTestbelege={leerNenntTestbelege}
                    filterZuruecksetzen={filterZuruecksetzen}
                    schalteTestbelegeEin={schalteTestbelegeEin}
                    q={filter.q || ''}
                    sucheLeeren={sucheLeeren}
                  />
                )}
                {!listeFehler && zeilen.map((g) => {
                  const kinder = g.kinder || []
                  const offen = istOffen(g)
                  const hinweis = gruppeHinweis(g.kopf.korrekturen)
                  const rest = g.kopf.korrekturen > 0 && g.kopf.rest != null
                    ? zahl(g.kopf.rest) : null
                  return (
                    <Fragment key={g.gruppe}>
                      <MobileRecordCard
                        data-testid="liste-karte"
                        title={g.kopf.id != null ? (
                          <Link
                            component={RouterLink}
                            to={`/invoicing/${g.kopf.id}`}
                            sx={{ fontWeight: 600, '&:focus-visible': focusRing() }}
                          >
                            {kopfText(g.kopf, g.bezug)}
                          </Link>
                        ) : (
                          <Typography component="span" sx={{ fontWeight: 600 }}>
                            {kopfText(g.kopf, g.bezug)}
                          </Typography>
                        )}
                        leading={kinder.length > 0 ? (
                          <IconButton
                            onClick={() => schalte(g)}
                            aria-label={offen ? 'Collapse corrections' : 'Expand corrections'}
                            aria-expanded={offen}
                            aria-controls={offen ? kinder.map(kinderId).join(' ') : undefined}
                            sx={{ width: AUFKLAPP_BREITE_MOBIL, height: AUFKLAPP_BREITE_MOBIL }}
                          >
                            {offen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                        ) : undefined}
                        actions={(
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: FS.base, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                              {zahl(g.kopf.brutto)}
                            </Typography>
                            {rest != null && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                                {`Remaining ${rest}`}
                              </Typography>
                            )}
                          </Box>
                        )}
                        meta={(
                          <>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              {g.kopf.kunde || STRICH}
                              {' · '}
                              {formatBerlinDate(g.kopf.datum, { dateOnly: true })}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{g.kopf.land || STRICH}</Box>
                              {' · '}
                              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                                {g.kopf.steuerfall
                                  ? <Fachbegriff label={STEUERFALL_LABEL[g.kopf.steuerfall] || g.kopf.steuerfall}
                                                 begriff={STEUERFALL_TOOLTIP[g.kopf.steuerfall] || 'Steuerfall'}
                                                 fokussierbar={false} />
                                  : STRICH}
                              </Box>
                            </Typography>
                            {/* S3b: dieselbe Angabe wie am Desktop, als eigene
                                `caption`-Zeile — bei 390 px bricht der Name NICHT
                                (Lehre 4/5), und die Karte bleibt ohne
                                waagerechten Ueberlauf. */}
                            {g.kopf.bestellung && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                Order{' '}
                                <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{g.kopf.bestellung}</Box>
                              </Typography>
                            )}
                            {gruppeMarke(g) && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                {gruppeMarke(g)}
                              </Typography>
                            )}
                            {hinweis && (
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                {hinweis}
                              </Typography>
                            )}
                          </>
                        )}
                        chips={(
                          <>
                            <StatusPill status={g.kopf.status} />
                            <Pill tone={bbTon(g.kopf.bb_state)} bordered nowrap>
                              {`BB: ${g.kopf.bb_state ? (BB_LABEL[g.kopf.bb_state] || g.kopf.bb_state) : STRICH}`}
                            </Pill>
                            <MailPill wert={g.kopf.mailstatus} />
                            {g.kopf.trifft && <Pill tone="info" bordered nowrap>Match</Pill>}
                          </>
                        )}
                        expanded={offen ? kinder.map((k) => (
                          <Box key={k.id} id={kinderId(k)} data-testid="gruppe-kind-karte">
                          <MobileRecordCard
                            variant="row"
                            title={(
                              <Link component={RouterLink} to={`/invoicing/${k.id}`}>
                                {k.nummer || 'without number'}
                              </Link>
                            )}
                            meta={(
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {k.kunde || STRICH}
                                {' · '}
                                {formatBerlinDate(k.datum, { dateOnly: true })}
                                {/* S3b: nur wenn die Nummer von der des Kopfes
                                    abweicht. Der Trenner gehoert zum FOLGENDEN
                                    Teil (Lehre 5) und steht mit im `nowrap`. */}
                                {k.bestellung && k.bestellung !== g.kopf.bestellung && (
                                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                                    {` · Order ${k.bestellung}`}
                                  </Box>
                                )}
                                {bezugText(k.beleg_bezug) ? ` · ${bezugText(k.beleg_bezug)}` : ''}
                              </Typography>
                            )}
                            chips={(
                              <>
                                <StatusPill status={k.status} />
                                <MailPill wert={k.mailstatus} />
                                {k.trifft && <Pill tone="info" bordered nowrap>Match</Pill>}
                              </>
                            )}
                            actions={(
                              <Typography sx={{ fontSize: FS.sm, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                {zahl(k.brutto)}
                              </Typography>
                            )}
                          />
                          </Box>
                        )) : undefined}
                      />
                    </Fragment>
                  )
                })}
              </Stack>
            )}

            {/* ── Fusszeile: NUR der Seitenbereich (Akzeptanz 2) ─────────────
                Den Zaehler traegt Z1 (`ResultCount`); „137 orders“ stand hier
                bis S3 ein zweites Mal.
                🔴 S3-NB1 (Z3, Nachtrag 01.11.): `TableFooterSimple` zeichnet die
                Leiste IMMER, den Blätterer aber nur ueber einer Seite — ohne
                ihn blieb ein leerer 44-px-Balken stehen (Foto
                `1440_liste-leer`; die Probe „1–1 of 1" fiel). Solange alles auf
                eine Seite passt, traegt deshalb `leftSlot` denselben
                Seitenbereich in derselben Form, die `TablePagination` sonst
                schreibt (`1–42 of 42`, Halbgeviertstrich). Kein zweiter Zaehler:
                der Bestand steht in Z1. */}
            <TableFooterSimple
              count={liste?.total || 0}
              page={Math.max(1, Number(liste?.page) || seite) - 1}
              rowsPerPage={liste?.per_page || PRO_SEITE}
              onPageChange={(p) => setzeSeite(p + 1)}
              leftSlot={seitenBereich(liste?.total, liste?.per_page || PRO_SEITE)}
            />

            {/* 🔴 Der Erklärblock steht UNTER der Tabelle (D13/W5, G7a).
                Über ihr schob er mit KPI-Zeile und Reiterleiste die erste
                Datenzeile aus dem Bild (P2); als Fussnote erklärt er die
                Spalten weiterhin vollständig. */}
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: SP.chip }}>
              One row per order — corrections are indented and shaded below their invoice. The metrics above count
              invoices, not rows. Gross: amount of the document (Forge, frozen), without currency ·
              Remaining: invoice gross minus its corrections · Throughput: the BB state last written by the
              receipt run and the mail status.
            </Typography>
          </Box>
        )}
        </>
      </ListToolbar>
    </>
  )
}

// ── Die Reiter der Seite (S5.7-F) ──────────────────────────────────────────

/**
 * Die drei Reiter. „Rechnungen“ ist die Vorgabe und steht OHNE Parameter in der
 * URL; „Betrieb“ ist `?tab=betrieb`, „Refund-Entscheidungen“ (N6) ist
 * `?tab=refunds`. Ein unbekannter Wert fällt auf die Liste zurück.
 *
 * 🔴 Der Untertitel gehört zum Reiter, der Kopf nicht: es gibt EINEN
 *    `ViewHeader` in der Schale (G7a, Grill B1). Bis G7a stand in jedem Reiter
 *    ein eigener Kopf; drei Köpfe mit demselben Titel über derselben Seite waren
 *    der Grund, die Reiterleiste unter den Titel zu ziehen. Der Untertitel ist
 *    die einzige Stelle, an der sich die drei noch unterscheiden — er bleibt
 *    deshalb erhalten — aber KURZ (UX-Gate W3, 2026-10-21): ein Satz, höchstens
 *    ~90 Zeichen. Die vier Abschnittsnamen der Operations-Seite standen früher
 *    aufgezählt im Untertitel (232 Zeichen) und wiederholten damit genau die
 *    Überschriften, die unmittelbar darunter stehen; auf 390 schob der Satz die
 *    KPI-Zone unter das erste Bild.
 */
const REITER = [
  {
    wert: 'rechnungen',
    label: 'Invoices',
    untertitel: 'Outgoing invoices issued by Forge to customers — read-only',
  },
  {
    wert: 'betrieb',
    label: 'Operations',
    untertitel: 'Operations of the invoice street — read-only, except for the manual invoice.',
  },
  {
    wert: 'refunds',
    label: 'Refund decisions',
    untertitel: 'Close refund decisions without a document — with reason and author. Only the open human list.',
  },
]
const REITER_WERTE = REITER.map((r) => r.wert)

/**
 * Die Kennung des Untertitels, den die Schale unter `lg` IM Panel rendert
 * (S2-NB3, 2026-11-01). Sie ist stabil und nicht je Reiter verschieden: es gibt
 * genau EINE Stelle, und das `aria-describedby` des `tabpanel` zeigt darauf.
 */
const UNTERTITEL_ID = 'kundenrechnungen-untertitel'

/** `PageContainer` blendet seine Kinder beim Einhängen ein (`fadeInUp`) — nach einem Wechsel nicht mehr. */
const OHNE_EINBLENDUNG = { '& > *': { animation: 'none' } }

/**
 * Die Seite `/invoicing`: die Weiche zwischen den Reitern.
 *
 * 🔴 **Ein Kopf für alle drei Reiter (G7a, Grill-B1).** Er steht in der SCHALE,
 *    damit die Zonenfolge `liste-kopf` → `liste-reiter` → `liste-panel` hält;
 *    stünde er im Reiter, läge `liste-kopf` INNERHALB von `liste-panel`. Der
 *    Untertitel ist je Reiter eine Variable, der Ausstellungsmodus ist ein
 *    `Pill` in `titleAdornment` (Status gehört dorthin, nicht in `actions`).
 *
 * 🔴 **S2 (2026-11-01): die KPI-Reihe steht im Panel von „Invoices“.** Bis S2
 *    stand sie zwischen Kopf und Reitern und galt damit für alle drei Reiter —
 *    das war der Grund, sie in die Schale zu ziehen (P3). Beides kehrt S2 um:
 *    es sind Rechnungs-Kennzahlen, also liegen sie unter den Reitern in dem
 *    Reiter, den sie beschreiben (`liste-kpi` **innerhalb** `liste-panel`).
 *    P3 ist keine Probe dieser Seite mehr; die Zusicherung ist eine eigene im
 *    Projekt `invoicing` (Containment, nicht `top`-Ordnung).
 *
 *    **Der Abruf bleibt in der Schale**, weil der Modus-Chip im Kopf auf allen
 *    drei Reitern sichtbar ist und dieselbe Antwort liest (`kennzahlen.modus`)
 *    — und weil er den URL-Zustand (`testbelege`) trägt. Deshalb ruft
 *    Operations zwei `/kennzahlen` (Schale für den Chip, Band gefiltert), und
 *    Refund decisions einen (nur für den Chip, ohne KPI-Reihe).
 *
 *    **Ungelesener Modus ⇒ kein Chip** (Regel 22 §3); die fünf Kacheln tragen
 *    dann fünf Striche.
 *
 * 🔴 **Die Reiter-Achse führt `useListQuery`** (`tabs`): es liest und schreibt
 *    `?tab=`, legt beim Wechsel einen Verlaufseintrag an (push, N10.2) und setzt
 *    `page` auf 1 (N10.3). Die alte Vokabel `?reiter=` wird EINMALIG auf `tab`
 *    gezogen (G5 §3.2a) — ein geteilter Link öffnet denselben Reiter, die
 *    Adresse trägt danach die neue Vokabel.
 *
 * 🔴 **Der Fokus braucht keinen Merker mehr.** Die alte MUI-`Tabs` wurde beim
 *    Wechsel neu montiert, deshalb holte ein Effekt den Fokus zurück (WCAG
 *    2.4.3). `UnderlineTabs` behält seine Reiterknoten — der aktivierte Reiter
 *    behält den Fokus von selbst (Klick wie Enter).
 *
 * Nur der gewählte Reiter ist gerendert — und nur er fragt seine Endpunkte.
 * Der `PageContainer` steht HIER und bleibt beim Wechsel stehen; seine
 * Einblendung läuft nur beim ersten Seitenaufbau (ein Reiterwechsel ist kein
 * neuer Seitenaufbau, auch „Zurück“/„Vor“ nicht).
 */
export default function InvoiceList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { state, set } = useListQuery({ tabs: REITER_WERTE })

  // 🔴 **EIN Schalter für die Seite (F2.7).** Er wirkt zusammen auf Liste,
  // Kennzahlen und Warnleiste — über denselben URL-Zustand `testbelege=1`.
  // Vorgabe AUS: die Tagesansicht zeigt die produktiven Rechnungen; die
  // Testbelege bleiben erreichbar und ihre Detailseiten voll benutzbar (F2.5).
  const testbelege = searchParams.get('testbelege') === '1'
  const setzeTestbelege = useCallback((an) => {
    const next = new URLSearchParams(searchParams)
    if (an) next.set('testbelege', '1')
    else next.delete('testbelege')
    next.delete('page')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Die alte Vokabel: `?reiter=betrieb` → `?tab=betrieb`. Gültig heisst: einer
  // der drei Werte. Der Reiter öffnet sich sofort (unten), die Adresse wird im
  // selben Aufbau umgeschrieben (`replace`, kein Verlaufseintrag).
  //
  // 🔴 Der Effekt schreibt `tab` NUR, wenn die Adresse kein GÜLTIGES trägt: sonst
  //    drehte er die Priorität der Zeile darunter um (`?tab=betrieb&reiter=refunds`
  //    zeigte Betrieb und landete dann auf Refunds). Ein UNBEKANNTER `reiter`
  //    fällt weg wie ein unbekannter `tab` (`useListQuery`, N10.4) — die Adresse
  //    trägt keine Vokabel, die die Seite nicht mehr kennt.
  //
  // 🔴 „Gültig", nicht „vorhanden" (UX-Gate W-N1, 2026-10-21): bei
  //    `?tab=foo&reiter=betrieb` galt das unbekannte `tab` als Priorität,
  //    `reiter` wurde gelöscht und der Hook räumte `foo` im selben Aufbau weg —
  //    beide Vokabeln weg, die Seite fiel auf den Vorgabereiter statt auf
  //    Betrieb. Jetzt gewinnt `reiter`, wenn `tab` keinen der drei Werte trägt.
  const altRoh = searchParams.get('reiter')
  const altGueltig = REITER.some((r) => r.wert === altRoh) ? altRoh : null
  const tabRoh = searchParams.get('tab')
  const tabGueltig = tabRoh != null && REITER_WERTE.includes(tabRoh)
  const reiter = !tabGueltig && altGueltig ? altGueltig : state.tab
  const index = Math.max(0, REITER.findIndex((r) => r.wert === reiter))

  useEffect(() => {
    if (altRoh == null) return
    const next = new URLSearchParams(searchParams)
    next.delete('reiter')
    if (altGueltig && !tabGueltig) {
      if (altGueltig === REITER_WERTE[0]) next.delete('tab')
      else next.set('tab', altGueltig)
    }
    setSearchParams(next, { replace: true })
  }, [altRoh, altGueltig, tabGueltig, searchParams, setSearchParams])

  const wechsle = useCallback((i) => {
    set('tab', REITER_WERTE[i] ?? REITER_WERTE[0])
  }, [set])

  // 🔴 R16 W1 über die Reiter-Achse des Hooks, nicht am Hook vorbei: `set('tab', …)`
  //    legt einen Verlaufseintrag an (N10.2) und setzt `page` auf 1 (N10.3). Der
  //    Direktschreiber im Reiter ließ auf Seite 3 `?tab=refunds&page=3` stehen —
  //    der Refund-Reiter liest `page` nicht, und beim Zurückwechseln stand die
  //    Liste wieder auf Seite 3.
  // 🔴 S1 (Kopf-Naht / Reiter-Anker): nach dem Wechsel auf den Refund-Reiter
  //    bekommt das PANEEL den Fokus, damit Tastatur und Screenreader ankommen.
  //    Ein Reiterwechsel ohne Fokus lässt den Fokus auf dem alten Reiter stehen —
  //    für einen Sprung aus dem Band ist das ein Ziel ohne Ankunft.
  const panelRef = useRef(null)
  // 🔴 R16-W3 (2026-11-01): das Paneel traegt denselben Sprung-Ring wie die
  //    Abschnitte — Merkmal setzen und Fokus im SELBEN Schritt, am selben Knoten.
  //    Vorher hing der Ring an `'&:focus'`; damit zeichnete ein gewoehnlicher
  //    Klick auf nicht fokussierbaren Inhalt AUSSERHALB jedes Abschnitts (etwa
  //    den Text des Bands „Needs attention") einen Rahmen um die ganze
  //    Inhaltsflaeche. Der naechste fokussierbare Vorfahre ist dort das Paneel.
  const panelSprung = useCallback(() => {
    const ziel = panelRef.current
    if (!ziel) return
    markiereSprungziel(ziel)
    ziel.focus()
  }, [])
  const zuRefunds = useCallback(() => {
    set('tab', 'refunds')
    // Nach dem Render: das Paneel des neuen Reiters ist dann montiert. Das
    // Paneel selbst wird beim Reiterwechsel NICHT ersetzt — `UnderlineTabs`
    // rendert den `tabpanel`-Kasten ohne `key`, und `liste-panel` steht als
    // festes Kind darin; nur sein Inhalt wechselt.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(panelSprung)
    } else {
      panelSprung()
    }
  }, [set, panelSprung])

  // 🔴 S3 (§6 Nr. 5): die Warnklasse `stichtag` traegt Bestell-IDs und bekommt
  //    deshalb KEINEN Detail-Link — ihr Weg ist der Sprung auf Operations, wo
  //    der Stichtag gesetzt wird. Dieselbe Form wie `zuRefunds`, dieselbe
  //    Fokussetzung auf das Paneel — und seit R16-Runde 5 (04.11.2026) auch
  //    dieselbe Markierung: `panelSprung` setzt Merkmal UND Fokus. Ein nacktes
  //    `focus()` liess den Ring aus, weil er seit R16-W3 am
  //    `data-sprungziel` haengt, nicht an `:focus`.
  const zuBetrieb = useCallback(() => {
    set('tab', 'betrieb')
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(panelSprung)
    } else {
      panelSprung()
    }
  }, [set, panelSprung])

  const [ersterReiter, setErsterReiter] = useState(reiter)
  const [einblenden, setEinblenden] = useState(true)
  if (einblenden && reiter !== ersterReiter) {
    // Zustand aus einer Änderung ableiten — das von React vorgesehene Muster
    // „während des Renderns anpassen“, kein Effekt (der käme einen Aufbau zu spät).
    setErsterReiter(reiter)
    setEinblenden(false)
  }

  // ── Die Meldung des Operations-Reiters an den Kopf (S1, Kopf-Naht) ───────
  // `Betrieb` meldet `{neuLaden, ladezeit, handfallSprung}` und beim Unmount
  // `null`. Der Setter ist stabil — `Betrieb` meldet in einem `useEffect` mit
  // genau dieser Abhängigkeit, kein Portal, kein zweiter Kopf.
  const [kopfMeldung, setKopfMeldung] = useState(null)

  // 🔴 S2-NB2 (2026-11-01): die Grenze ist `lg`, NICHT die von `ViewHeader`
  //    (`isMobile` = `down('sm')`). Sie misst hier die BREITE DES KOPFES, nicht
  //    die Telefonbreite: ab `sm` steht die Seitenleiste (x = 284), und der
  //    Kopf ist der Rest. Bei 600 Pixel bleiben ihm rund 280 px, bei 900 rund
  //    572 px — Titel, Modus-Chip und die drei Operations-Aktionen brauchen
  //    rund 640 px. Darunter bricht der Kopf nur auf DIESEM Reiter um und
  //    schiebt die Reiterleiste (gemessen bei 600 px: y 231 auf Invoices und
  //    Refund decisions, 371 auf Operations; bei 390 px: 170 auf 224, also
  //    54 px). Ab 1200 px bleiben rund 870 px, dort trägt der Kopf sie wieder.
  //    `ViewHeader.jsx` bleibt unberührt — hier entscheidet die Zahl nur den
  //    ORT der Operations-Aktionen (Kopf oder Panel).
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('lg'))

  // ── Kennzahlen des Berliner Tages (Zone 2, für alle drei Reiter) ─────────
  const [kennzahlen, setKennzahlen] = useState(null)
  const [kennzahlenLaedt, setKennzahlenLaedt] = useState(true)
  const [kennzahlenFehler, setKennzahlenFehler] = useState(null)

  const ladeKennzahlen = useCallback(() => {
    let abgemeldet = false
    setKennzahlenLaedt(true)
    api.get('/api/invoicing/admin/kennzahlen', {
      params: testbelege ? { testbelege: 1 } : {},
    })
      .then(({ data }) => { if (!abgemeldet) { setKennzahlen(data); setKennzahlenFehler(null) } })
      .catch((e) => {
        if (abgemeldet) return
        // 🔴 Die alten Zahlen fallen weg: eine Kachel, deren Abruf gerade
        //    scheiterte, darf keinen Stand zeigen, der nicht mehr gilt.
        setKennzahlen(null)
        setKennzahlenFehler(e?.response?.data?.error || e?.message || 'Unknown error')
      })
      .finally(() => { if (!abgemeldet) setKennzahlenLaedt(false) })
    return () => { abgemeldet = true }
  }, [testbelege])

  // Einmal je Mount; der Aufräumer wird EINGESAMMELT, nicht weggeworfen.
  useEffect(() => {
    const ab = ladeKennzahlen()
    return () => { ab() }
  }, [ladeKennzahlen])

  // ── Der Ausstellungsmodus als Chip im Kopf (S2, W13) ─────────────────────
  // 🔴 **Dieselbe Antwort wie die Kacheln im Panel** (`kennzahlen.modus`): es
  //    ist EIN Abruf. Er bleibt in der Schale, weil der Chip auf ALLEN drei
  //    Reitern steht und den URL-Zustand (`testbelege`) trägt; nur die
  //    KPI-Reihe zieht in das Panel von „Invoices“ (Orchestrator-Entscheid
  //    01.11., §5 S2 — der Plan selbst führt beides und es geht nicht beides).
  //    Operations und Refund decisions tragen damit einen `/kennzahlen`-Abruf
  //    für den Chip, aber keine KPI-Reihe.
  //
  // 🔴 **Ungelesen ⇒ KEIN Chip** (Regel 22 §3): kein „—", kein leerer Rahmen.
  //    Ein Modus, der nicht gelesen wurde, ist nicht „Off".
  const modus = kennzahlen?.modus || {}
  const modusGelesen = typeof modus.issue === 'string' && modus.issue !== ''
  const modusChip = modusGelesen ? (
    <Pill
      tone="plain"
      title={`Issuance: ${modusText(modus.issue)}${modus.correction == null ? '' : ` · Correction: ${modusText(modus.correction)}`}`}
    >
      {`Issuance ${modusText(modus.issue)}${modus.correction == null ? '' : ` · Correction ${modusText(modus.correction)}`}`}
    </Pill>
  ) : null

  return (
    <PageContainer width="full" boxSx={einblenden ? undefined : OHNE_EINBLENDUNG}>
      {/* Zone 1 — Kopf. EIN Kopf für alle drei Reiter.
          🔴 S1 (Kopf-Naht): auf Operations meldet `Betrieb` seine zwei
          Kopf-Aktionen („Manual invoice“ und der Refresh) und die Ladezeit nach
          oben; `InvoiceList` baut daraus NUR Bedienung. Die Hooks bleiben in
          `Betrieb` — zöge man sie hoch, lüden die zwei anderen Reiter nichts.
          🔴 S2-NB2 (2026-11-01): UNTER `lg` (1200 px) trägt der Kopf diese
          Aktionen NICHT mehr. Der Grund ist die Kopfbreite, nicht die
          Telefonbreite: ab `sm` steht die Seitenleiste (x = 284), bei 600 px
          bleiben dem Kopf rund 280 px, bei 900 rund 572 px — Titel, Modus-Chip
          und die drei Aktionen brauchen rund 640 px. Der Kopf brach darunter
          nur auf diesem Reiter um und schob die Reiterleiste (600 px: y 231 auf
          Invoices und Refund decisions, 371 auf Operations). Die Aktionen
          stehen dort im Panel (`liste-betrieb-aktionen`). Ab 1200 px bleiben
          rund 870 px; dort ist der Kopf unverändert. `actionsPending` entfällt,
          weil es nur die Zeile im Kopf reservierte, die es schmal nicht mehr
          gibt. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="Customer Invoices"
          subtitle={istSchmal ? undefined : REITER[index].untertitel}
          titleAdornment={modusChip}
          actions={!istSchmal && kopfMeldung ? [
            { label: 'Manual invoice', variant: 'outlined', onClick: kopfMeldung.handfallSprung },
            { iconOnly: true, label: 'Refresh invoice operations', onClick: kopfMeldung.neuLaden },
          ] : []}
          rightAdornment={!istSchmal && kopfMeldung?.ladezeit ? (
            <Typography variant="caption" color="text.secondary" data-testid="liste-ladezeit">
              Loaded {formatTime(kopfMeldung.ladezeit)}
            </Typography>
          ) : null}
        />
      </Box>

      {/* 🔴 S2 (2026-11-01): die KPI-Reihe steht NICHT mehr hier, sondern als
          Z0 IM Panel von „Invoices“ (`liste-kpi` innerhalb `liste-panel`).
          Damit ist die Zonenfolge `liste-kopf` → `liste-reiter` →
          `liste-panel`; der Kopf trägt den Modus als Chip. */}

      {/* Zone 3 — Reiter. `withPanel` gibt dem gewählten Reiter `aria-controls`
          und dem Inhalt `role="tabpanel"`; die Marke `liste-reiter` umschliesst
          die Reihe, `liste-panel` sitzt IM Paneel (P8 sucht den Zähler dort).
          🔴 R15 W7 (2026-11-01): `panelAriaDescribedBy` zeigt auf ein KIND dieses
          Panels. ARIA-konform, aber ein Screenreader liest den Satz damit
          zweimal — einmal als Inhalt, einmal als Beschreibung. Das ist hier
          HINGENOMMEN, keine Selbstverständlichkeit: die Beschreibung dient der
          Ansage beim Sprung ins Paneel (nicht beim blossen Reiterwechsel — der
          wechselt nur `aria-selected`), und der Satz ist ohnehin Panel-Inhalt. Das
          Haus benennt denselben Effekt an `component-registry.md:1748`
          (DisabledReason: „sonst laese ein Screenreader den Grund zweimal"). */}
      <Box data-testid="liste-reiter" sx={{ mt: SP.card }}>
        <UnderlineTabs
          tabs={REITER.map((r) => r.label)}
          value={index}
          onChange={wechsle}
          withPanel
          ariaLabel="View"
          panelIdPrefix="kundenrechnungen"
          panelAriaDescribedBy={istSchmal ? UNTERTITEL_ID : undefined}
        >
          {/* 🔴 `tabIndex={-1}` macht das Paneel zum Ankerziel eines Reiter-Sprungs
              (S1): `zuRefunds` setzt den Fokus, damit Tastatur und Screenreader
              ankommen. 🔴 Der Ring hängt seit R16-W3 (2026-11-01) am
              SPRUNG-MERKMAL `data-sprungziel`, nicht an `:focus` — sonst trüge
              das Paneel einen Rahmen, sobald ein gewöhnlicher Klick auf nicht
              fokussierbaren Inhalt außerhalb jedes Abschnitts es fokussiert
              (etwa der Text des Bands „Needs attention"). Dieselbe Form wie in
              `Betrieb.jsx`; die Mechanik steht in `utils/sprungziel.js`. */}
          <Box
            data-testid="liste-panel"
            tabIndex={-1}
            ref={panelRef}
            sx={{ '&[data-sprungziel="aktiv"]': focusRing() }}
          >
            {/* 🔴 S2-NB3 (2026-11-01): unter `lg` steht der Untertitel HIER, als
                erste Zeile des Panels — nicht mehr im Kopf. Grund ist gemessen:
                die drei Texte sind 58 / 76 / 93 Zeichen lang und brachen im
                Kopf je Reiter verschieden um; bei 600 px kostete „Refund
                decisions“ eine Zeile mehr als die zwei anderen und schob die
                Reiterleiste um 17 px (`y` 248 gegen 231). Der Kopf ist damit
                oberhalb der Reiterleiste wieder für alle drei Reiter gleich.
                🔴 **Eine Stelle, nicht drei:** der Text kommt aus `REITER[index]`
                in der Schale; je Reiter eine Kopie liefe auseinander.
                🔴 Wortlaut unverändert und KEINE reservierte Höhe: eine feste
                Höhe oder ein `line-clamp` kostete Text.
                🔴 Ab `lg` steht er im Kopf (`ViewHeader`, `subtitle`) — dort
                passen alle drei in eine Zeile, und der Kopf ist byte-gleich. */}
            {istSchmal && (
              <Typography
                id={UNTERTITEL_ID}
                data-testid="liste-untertitel"
                variant="body2"
                color="text.secondary"
                sx={{ mb: SP.card }}
              >
                {REITER[index].untertitel}
              </Typography>
            )}
            {/* 🔴 S2-NB2 (2026-11-01): unter `lg` (1200 px) stehen die
                Operations-Aktionen HIER statt im Kopf — sonst trüge nur dieser
                Reiter eine Aktionszeile und der Reiterwechsel verschöbe die
                Reiterleiste.
                Die Zeile reserviert ihre Höhe, solange `Betrieb` noch nichts
                gemeldet hat (`FIELD_HEIGHT_SX`); ohne die Reservierung spränge
                der Panelinhalt beim Eintreffen der Meldung. */}
            {reiter === 'betrieb' && istSchmal && (
              <Box
                data-testid="liste-betrieb-aktionen"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SP.tight,
                  minHeight: FIELD_HEIGHT_SX.minHeight,
                  mb: SP.card,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {kopfMeldung?.ladezeit ? (
                    <Typography variant="caption" color="text.secondary" data-testid="liste-ladezeit">
                      Loaded {formatTime(kopfMeldung.ladezeit)}
                    </Typography>
                  ) : null}
                </Box>
                {kopfMeldung ? (
                  <>
                    <Button variant="outlined" onClick={kopfMeldung.handfallSprung}>
                      Manual invoice
                    </Button>
                    <RefreshButton
                      ariaLabel="Refresh invoice operations"
                      onClick={kopfMeldung.neuLaden}
                    />
                  </>
                ) : null}
              </Box>
            )}
            {reiter === 'betrieb'
              ? <Betrieb meldeKopf={setKopfMeldung} zuRefunds={zuRefunds} />
              : reiter === 'refunds'
                ? <RefundEntscheidungen setzeTestbelege={setzeTestbelege} />
                : (
                  <RechnungenReiter
                    kennzahlen={kennzahlen}
                    kennzahlenLaedt={kennzahlenLaedt}
                    kennzahlenFehler={kennzahlenFehler}
                    ladeKennzahlen={ladeKennzahlen}
                    setzeTestbelege={setzeTestbelege}
                    zuRefunds={zuRefunds}
                    zuBetrieb={zuBetrieb}
                  />
                )}
          </Box>
        </UnderlineTabs>
      </Box>
    </PageContainer>
  )
}
